import {
  branches,
  confirmedRequirements,
  conferences,
  membershipFlow,
  navItems,
  primaryNavItems,
  secondaryNavItems,
  notices,
  participantInfoItems,
  partySections,
  paymentStates,
  userBindingFlow
} from '../data/siteData'
import { PUBLISH_NAV_BOARDS } from '../data/navMenus'
import { findPartySectionByPath } from '../data/partyNav'
import {
  getAttachments,
  getBoardCovers,
  getGalleryImportantEvent,
  getHistoryTime,
  getOriginalFile,
  preferHomepageEntries
} from '../utils/cmsExtra'
import { validatePasswordPolicy } from '../utils/passwordPolicy'
import {
  addMyApplication,
  addMyPayment,
  addMyRegistration,
  bindBranch,
  cancelAccount,
  cancelMyApplication,
  cancelMyPayment,
  changePassword,
  clearToken,
  forgotPassword,
  getMembershipBankAccount,
  getMembershipFeeConfig,
  getMyMemberProfile,
  getPublicMembershipTemplates,
  getPublicCmsEntry,
  getPublicChannel,
  getPublicContent,
  getPublicFileDownloadInfo,
  getUserProfile,
  listBindableAssociations,
  listMyContents,
  listMyApplications,
  listMyBindings,
  listMyPayments,
  listMyNotifications,
  markMyNotificationsRead,
  listMyRegistrations,
  listOpenConferences,
  listPublicAssociations,
  listPublicChannels,
  listPublicCmsEntries,
  listPublicContents,
  listPublicFiles,
  unwrapPublicPage,
  applyClientPageFallback,
  login,
  logoutApi,
  register,
  saveToken,
  startCstCloudLogin as startCstCloudLoginApi,
  sendEmailCode,
  setUnauthorizedHandler,
  unbindBranch,
  updateMyParticipant,
  deleteMyAbstract,
  updateUserProfile,
  updateUserType,
  getConferenceMaterials,
  getConferenceFeeQuote,
  getMembershipStatus,
  uploadMyApplicationFile,
  uploadMyPaymentFile,
  uploadMyRegistrationFile,
  downloadSecureFile,
  downloadStorageFile,
  fetchSecureFileObjectUrl,
  resolveDownloadErrorMessage,
  requestOrganizationMembership
} from '../api/paleo'
import {
  isStorageOrProfilePath,
  toSiteRelativeFilePath
} from '../utils/siteFileUrl'
import {
  currentRelativePath,
  getAuthorizationUrl,
  isSafeAuthorizationUrl
} from '../utils/cstCloudAuth'

const BRANCH_CODE_BY_ASSOCIATION_ID = {
  1: 'zgswxh',
  2: 'gwjzdwxfh',
  3: 'kpgzwyh',
  4: 'bfxfh',
  5: 'wtxfh',
  6: 'hszlzwyh',
  7: 'gzwxfh',
  8: 'dqswx',
  9: 'gst',
  10: 'gjzdw',
  11: 'swcj',
  12: 'xjsxff',
  13: 'jygzwyh',
  14: 'ttswxfh'
}

const readJsonStorage = (key, fallback) => {
  try {
    return JSON.parse(localStorage.getItem(key) || JSON.stringify(fallback))
  } catch (error) {
    return fallback
  }
}

const SELF_SERVICE_CACHE_KEY = 'paleo_self_service_snapshot_v1'

const readSelfServiceSnapshot = () => {
  try {
    const raw = sessionStorage.getItem(SELF_SERVICE_CACHE_KEY)
    if (!raw) {
      return null
    }
    const snap = JSON.parse(raw)
    if (!snap || !snap.userInfo) {
      return null
    }
    return snap
  } catch (error) {
    return null
  }
}

const clearSelfServiceSnapshot = () => {
  try {
    sessionStorage.removeItem(SELF_SERVICE_CACHE_KEY)
  } catch (error) {
    // ignore storage failures
  }
}

export default {
  data() {
    const tokenPresent = Boolean(localStorage.getItem('paleo_user_token'))
    const selfServiceSnapshot = tokenPresent ? readSelfServiceSnapshot() : null
    return {
      activeSection: 'home',
      activeTopicModule: 'timeline',
      branches,
      confirmedRequirements,
      conferences,
      membershipFlow,
      navItems,
      primaryNavItems,
      secondaryNavItems,
      notices,
      channels: [],
      activeBlocks: [],
      publicPageError: '',
      mobileNavOpen: false,
      notificationOpen: false,
      language: localStorage.getItem('paleo-front-language') || 'zh',
      readNotificationIds: readJsonStorage('paleo-front-read-notices', []),
      searchKeyword: '',
      searchSubmittedKeyword: '',
      searchResults: [],
      searchLoading: false,
      dynamicNotices: [],
      myContents: [],
      dynamicCmsEntries: [],
      activeBannerIndex: 0,
      bannerTimer: null,
      siteConfigTimer: null,
      siteConfigRefreshPending: false,
      siteConfig: {
        copyright: '© 2026 中国古生物学会 版权所有',
        contactPhone: '',
        contactEmail: '',
        address: '',
        postcode: '',
        fax: '',
        icp: '',
        policeRecord: '',
        wechatQrcode: '',
        weiboQrcode: '',
        memberQrcode: '',
        friendLinks: [],
        themePreset: 'academic',
        themeColor: '#002B49',
        galleryHistorySort: 'asc',
        contactUsHtml: ''
      },
      quickLinks: [],
      contactUsOpen: false,
      quickEntryCards: [
        { label: '学会简介', path: '/intro', icon: 'intro' },
        { label: '组织机构', path: '/structure', icon: 'structure' },
        { label: '学会服务', path: '/services', icon: 'services' },
        { label: '党建文化', path: '/party', icon: 'party' }
      ],
      selectedContent: null,
      contentDetailLoading: false,
      contentDetailError: '',
      publicFiles: [],
      publicFilesPageNum: 1,
      publicFilesPageSize: 5,
      publicFilesTotal: 0,
      publicFilesLoadSeq: 0,
      selectedCmsEntry: null,
      activePublicFileCategory: 'ALL',
      publicFileCategories: [
        { label: '全部', value: 'ALL' },
        { label: '文档', value: 'DOCUMENT' },
        { label: '音频', value: 'AUDIO' },
        { label: '视频', value: 'VIDEO' },
        { label: '照片', value: 'IMAGE' }
      ],
      cmsSections: [
        { type: 'MEETING_NOTICE', label: '会议通知', legacy: ['NOTICE'] },
        { type: 'IMPORTANT_NEWS', label: '重要新闻', legacy: ['ARTICLE'] },
        { type: 'PARTY_PUBLIC', label: '党务公开', legacy: ['PARTY'] },
        { type: 'SOCIETY_DYNAMICS', label: '学会动态', legacy: [] },
        { type: 'WORK_PROGRESS', label: '工作进展', legacy: [] }
      ],
      cmsFeatureModules: [
        { moduleCode: 'pages', label: '学会简介', scopeLabel: '概况' },
        { moduleCode: 'awards', label: '获奖成果', scopeLabel: '成果' },
        { moduleCode: 'personnel', label: '组织人员', scopeLabel: '机构' },
        { moduleCode: 'branch', label: '分会内容', scopeLabel: '分会' },
        { moduleCode: 'science', label: '科学传播', scopeLabel: '科普' },
        { moduleCode: 'tech-rewards', label: '科技奖励', scopeLabel: '奖励' },
        { moduleCode: 'timeline', label: '学会沿革', scopeLabel: '历史' },
        { moduleCode: 'gallery', label: '历史相册', scopeLabel: '相册' },
        { moduleCode: 'international', label: '国际交流', scopeLabel: '交流' },
        { moduleCode: 'regulations', label: '规章条例', scopeLabel: '制度' }
      ],
      participantInfoItems,
      partySections,
      partyListItems: [],
      partyListTotal: 0,
      partyListPageNum: 1,
      partyListPageSize: 5,
      partyListLoadSeq: 0,
      partyTopicDetail: null,
      partyTopicArticles: [],
      paymentStates,
      userBindingFlow,
      bindableAssociations: [],
      boundBranchCodes: (selfServiceSnapshot && selfServiceSnapshot.boundBranchCodes) || [],
      myBindings: [],
      publicConferences: [],
      selectedConference: null,
      selectedConferenceFeeQuote: null,
      selectedMaterials: {},
      myApplications: (selfServiceSnapshot && selfServiceSnapshot.myApplications) || [],
      myPayments: (selfServiceSnapshot && selfServiceSnapshot.myPayments) || [],
      myRegistrations: [],
      myNotifications: [],
      unreadNotificationCount: 0,
      /** 登录后须等通知接口返回再展示角标，避免静态占位公告误计未读 */
      notificationsSynced: !tokenPresent,
      // 身份不走 session 快照：退会等变更后旧 userType 会导致刷新闪出入会流程
      memberProfile: null,
      selfServiceLoading: tokenPresent,
      /** 登录后须等首包身份接口返回再展示路径相关 UI，避免缓存闪错页 */
      membershipSessionSynced: !tokenPresent,
      selfServiceStatus: '',
      isLoggedIn: tokenPresent,
      userInfo: null,
      accountDropdownOpen: false,
      authFeedback: '',
      authFeedbackDialogOpen: false,
      membershipChoiceSubmitting: false,
      membershipEligibility: null,
      withdrawWizardOpen: false,
      forgotPasswordGuideOpen: false,
      forgotPasswordEmail: '',
      forgotPasswordCode: '',
      forgotPasswordNew: '',
      forgotPasswordConfirm: '',
      forgotCodeSeconds: 0,
      forgotCodeTimer: null,
      forgotCodeSending: false,
      authSubmitting: false,
      cstCloudStarting: false,
      cstCloudUnavailableOpen: false,
      cstCloudUnavailableMessage: '',
      emailCodeSeconds: 0,
      emailCodeTimer: null,
      emailCodeSending: false,
      registerEmailCodeReady: false,
      registerEmailCodeEmail: '',
      membershipPath: '',
      activeServiceTab: 'main',
      activeServiceBranchId: '',
      serviceTabs: [
        { label: '服务大厅', value: 'main', icon: 'home' },
        { label: '会员服务', value: 'member', icon: 'member' },
        { label: '专业分会', value: 'branches', icon: 'branches' },
        { label: '学术会议', value: 'conference', icon: 'conference' },
        { label: '科学传播', value: 'science', icon: 'megaphone' },
        { label: '科技奖励', value: 'rewards', icon: 'medal' },
        { label: '国际交流', value: 'international', icon: 'globe' }
      ],
      serviceProgressMap: {
        main: [
          { title: '选择业务', desc: '从分会、会员、会议和公共服务进入办理路径' },
          { title: '登录办理', desc: '需要提交或上传资料时通过顶栏登录/注册' },
          { title: '个人中心回看', desc: '办理记录和资料状态集中在个人中心查看' }
        ],
        branches: [
          { title: '选择分会', desc: '按研究方向绑定一个或多个分会' },
          { title: '接收通知', desc: '前台会议与通知按绑定分会展示' },
          { title: '后续办理', desc: '继续入会、缴费或会议报名' }
        ],
        member: [
          { title: '提交申请', desc: '选择入会或退会并上传申请书' },
          { title: '秘书处审核', desc: '管理员审核申请材料和会员身份' },
          { title: '缴费确认', desc: '通过后进入会员费线下缴费和凭证上传' }
        ],
        conference: [
          { title: '选择会议并报名', desc: '按身份自动匹配费用类型并锁定金额' },
          { title: '缴费两阶段', desc: '线下转账后上传凭证初审、发票终审' },
          { title: '参会资料', desc: '凭证初审通过后可填基础信息、摘要与住宿；野外等需发票终审通过' }
        ],
        international: [
          { title: '查看交流动态', desc: '读取国际交流 CMS 栏目内容' },
          { title: '关注合作机会', desc: '后续由后台维护会议、合作和访问资讯' },
          { title: '联系学会', desc: '通过页脚联系方式咨询具体事项' }
        ],
        science: [
          { title: '查看科普内容', desc: '读取科学传播 CMS 栏目内容' },
          { title: '关注活动', desc: '展示科普活动、文章和资料' },
          { title: '持续更新', desc: '后台发布后自动进入前台展示' }
        ],
        rewards: [
          { title: '查看奖励信息', desc: '读取科技奖励 CMS 栏目内容' },
          { title: '了解申报要求', desc: '后续发布奖项通知和申报材料' },
          { title: '下载资料', desc: '关联公开文件下载区获取附件' }
        ]
      },
      offlinePaymentInfo: {
        account: '收款账户以会议通知或后台公布信息为准。',
        remark: '转账备注建议填写：姓名 + 手机号 + 会员费/会议名称。',
        upload: '转账后请在当前页面上传缴费凭证；如需发票，同步上传发票材料。'
      },
      membershipBankAccount: (selfServiceSnapshot && selfServiceSnapshot.membershipBankAccount) || null,
      membershipFeeConfigs: (selfServiceSnapshot && Array.isArray(selfServiceSnapshot.membershipFeeConfigs))
        ? selfServiceSnapshot.membershipFeeConfigs
        : [],
      membershipTemplates: (selfServiceSnapshot && selfServiceSnapshot.membershipTemplates) || { join: null, withdraw: null },
      authMode: 'login',
      authDialogOpen: false,
      pwdWarn: false,
      pwdDaysLeft: null,
      registerEmailOtpHint: '验证码将发送至您填写的邮箱，请查收后填写；如未收到，请检查垃圾邮件箱。',
      authForm: {
        account: '',
        password: '',
        confirmPassword: '',
        slideTicket: '',
        emailOtp: '',
        name: '',
        gender: '',
        unit: '',
        orgCreditCode: '',
        role: '教师',
        title: ''
      },
      applicationForm: {
        applicationType: 'JOIN',
        joinGroup: 'annual',
        memberCategory: '',
        applicantName: '',
        foreignFlag: false,
        termStartDate: '',
        termEndDate: ''
      },
      profileForm: {
        userName: '',
        gender: '',
        unit: '',
        orgCreditCode: '',
        roleLabel: '教师',
        title: '',
        email: '',
        memberCategory: 'NON_STUDENT'
      },
      passwordForm: {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      cancelAccountForm: {
        password: '',
        confirmText: '',
        acknowledged: false
      },
      paymentForm: {
        applicationId: null,
        memberCategory: 'STUDENT',
        amount: 0
      },
      /** 缴费向导会费类型 ANNUAL | PERMANENT */
      paymentFeeChoice: 'ANNUAL',
      /** 会议费缴费方式 BANK_TRANSFER | OFFICIAL_CARD_ONSITE */
      conferencePaymentMethod: 'BANK_TRANSFER',
      participantForm: {
        registrationId: null,
        participantName: '',
        participantSex: '',
        participantUnit: '',
        participantRole: '',
        sessionName: '',
        reportType: 'ORAL',
        reportTitle: '',
        accommodationType: 'SELF',
        fieldTripStage: '',
        fieldTripRoute: '',
        fieldTripSelections: ''
      },
      uploadSelection: {
        applicationId: null,
        paymentId: null,
        registrationId: null,
        registrationFileRole: 'voucher'
      },
      selectedRegistrationId: null,
      conferenceSubView: 'list',
      noticePreviewConferenceId: null,
      /** 会议报名/缴费操作反馈（列表与详情页可见） */
      conferenceFeedback: '',
      observer: null
    }
  },
  computed: {
    page() {
      return (this.$route.meta && this.$route.meta.page) || 'home'
    },
    contentId() {
      return this.page === 'content-detail' ? String(this.$route.params.id || '') : ''
    },
    conferenceId() {
      return this.page === 'conference-detail' ? String(this.$route.params.id || '') : ''
    },
    authFeedbackIsError() {
      const text = this.authFeedback || ''
      return !/成功|已就绪|已修改|已重置|已发送|请查收/.test(text)
    },
    authFeedbackClass() {
      return {
        success: !this.authFeedbackIsError,
        warning: this.authFeedbackIsError
      }
    },
    languageLabel() {
      return this.language === 'zh' ? 'EN' : '中文'
    },
    systemNotifications() {
      // 未登录收不到通知；登录后：个人站内信 + 公开公告
      if (!this.isLoggedIn) {
        return []
      }
      const personal = (this.myNotifications || []).map(item => ({
        id: `personal-${item.notificationId}`,
        title: item.title || '系统提醒',
        type: this.notificationCategoryLabel(item.category),
        date: item.createTime || '刚刚',
        personalNotificationId: item.notificationId,
        isRead: item.readFlag === '1',
        raw: item
      }))
      const personalIds = new Set(personal.map(item => item.id))
      const publicSource = this.homepagePublishSorted.length ? this.visibleNotices : []
      const publicNotices = publicSource.slice(0, 3).map((item, index) => {
        const id = String(item.entryId || item.contentId || `${item.title}-${item.date}-${index}`)
        return {
          id,
          title: item.title || '系统通知',
          type: item.type || '通知',
          date: item.date || '待发布',
          isRead: this.readNotificationIds.includes(id),
          raw: item
        }
      }).filter(item => !personalIds.has(item.id))
      return [...personal, ...publicNotices]
    },
    unreadNoticeCount() {
      // 未登录无角标；登录后须等通知同步完成，且公开公告仅计 CMS 真实条目
      if (!this.isLoggedIn || !this.notificationsSynced) {
        return 0
      }
      const personalUnread = Number(this.unreadNotificationCount || 0)
      const publicUnread = this.homepagePublishSorted.length
        ? this.systemNotifications
          .filter(item => !item.personalNotificationId && !this.readNotificationIds.includes(item.id))
          .length
        : 0
      return personalUnread + publicUnread
    },
    activeChannel() {
      return this.channels.find(item => this.channelCodeForPage(item) === this.page || item.routePath === this.$route.path) || null
    },
    displayNavItems() {
      return this.primaryNavItems
    },
    partyNavItems() {
      return this.partySections
    },
    pageTitle() {
      if (this.page === 'news-publish') {
        const boardCode = this.$route && this.$route.query && this.$route.query.board
        const board = PUBLISH_NAV_BOARDS.find(item => item.code === boardCode)
        if (board) {
          return board.label
        }
      }
      return this.staticPageTitle(this.page)
    },
    pageSubtitle() {
      if (this.isPartyHub) {
        return '把组织生活、学习材料、先进典型和举报入口统一呈现'
      }
      if (this.showPartySidebar) {
        const ctx = this.activePartyContext
        if (ctx) {
          return ctx.subtitle || ''
        }
        const section = this.partyNavItems.find(item => this.isPartyNavActive(item))
        return (section && section.subtitle) || ''
      }
      return ''
    },
    pageKicker() {
      // 不再与面包屑/主标题重复展示小标签
      return ''
    },
    layoutCode() {
      return {
        history: 'editorial',
        gallery: 'gallery',
        international: 'standard',
        regulations: 'standard'
      }[this.page] || 'standard'
    },
    shellType() {
      return this.isPartyPage ? 'party' : 'main'
    },
    pageType() {
      return {
        history: 'timeline',
        gallery: 'gallery',
        international: 'list',
        regulations: 'list'
      }[this.page] || 'page'
    },
    pageBreadcrumb() {
      return this.staticPageTitle(this.page)
    },
    /** 面包屑路径项：最后一项为当前位置（标红） */
    breadcrumbTrail() {
      const home = { label: '首页', path: '/', current: false }
      if (this.page === 'party') {
        return [
          home,
          { label: '党建文化', path: '/party', current: false },
          { label: '党建文化中心', path: null, current: true }
        ]
      }
      if (this.showPartySidebar) {
        const ctx = this.activePartyContext
        if (!ctx || !ctx.section) {
          return [
            home,
            { label: '党建文化', path: '/party', current: false },
            { label: this.pageBreadcrumb, path: null, current: true }
          ]
        }
        const trail = [
          home,
          { label: '党建文化', path: '/party', current: false },
          {
            label: ctx.section.label,
            path: (ctx.child || (this.$route.params && this.$route.params.entryId)) ? ctx.section.path : null,
            current: !(ctx.child || (this.$route.params && this.$route.params.entryId))
          }
        ]
        if (ctx.child) {
          trail.push({
            label: ctx.child.label,
            path: null,
            current: !(this.$route.params && this.$route.params.entryId && this.activePartyTopic)
          })
        }
        if (this.page === 'special-topics' && this.$route.params.entryId && this.activePartyTopic) {
          trail.forEach(t => { t.current = false })
          trail[2].path = ctx.section.path
          trail.push({ label: this.activePartyTopic.title, path: null, current: true })
        }
        return trail
      }
      if (this.page === 'branch-site') {
        const branchId = Number((this.$route.params && this.$route.params.branchId) || 0)
        const resolved = this.associationNameById(branchId)
        const branchLabel = resolved && !String(resolved).startsWith('学会/分会') ? resolved : '专业分会'
        return [
          home,
          { label: '组织机构', path: '/structure', current: false },
          { label: branchLabel, path: null, current: true }
        ]
      }
      if (this.page === 'news-publish') {
        const boardCode = this.$route && this.$route.query && this.$route.query.board
        const board = PUBLISH_NAV_BOARDS.find(item => item.code === boardCode)
        if (board) {
          return [
            home,
            { label: '通知要闻', path: '/news-publish', current: false },
            { label: board.label, path: null, current: true }
          ]
        }
      }
      return [home, { label: this.pageBreadcrumb, path: null, current: true }]
    },
    hidePageMainTitle() {
      // 学会服务/公开文件标题区已去掉；党建/个人中心/分会子站标题在页内自带
      return this.page === 'personal-center'
        || this.page === 'branch-site'
        || this.page === 'services'
        || this.page === 'public-downloads'
        || this.page === 'downloads-center'
        || this.isPartyPage
    },
    showPageMasthead() {
      return !this.isHomePage && !this.isNotFoundPage && !this.isContentDetailPage && !this.isConferenceDetailPage
    },
    isHomePage() {
      return this.page === 'home'
    },
    isIntroPage() {
      return this.page === 'home' || this.page === 'intro'
    },
    isNewsPage() {
      return ['home', 'news', 'news-publish', 'society-announcements', 'content-detail', 'branch-site'].includes(this.page)
    },
    isContentDetailPage() {
      return this.page === 'content-detail'
    },
    isPartyPage() {
      return this.page === 'party' || this.showPartySidebar
    },
    isPartyRoute() {
      return [
        'party',
        'announcements',
        'organizations',
        'committees',
        'work',
        'activities',
        'team-building',
        'theory-study',
        'dynamics',
        'special-topics',
        'exemplars',
        'reporting',
        'party-downloads'
      ].includes(this.page)
    },
    showPartySidebar() {
      return this.isPartyRoute
    },
    isPartyHub() {
      return this.page === 'party'
    },
    isTopicPage() {
      return ['history', 'gallery', 'international', 'regulations'].includes(this.page)
    },
    activeTopicModuleCode() {
      return {
        history: 'timeline',
        gallery: 'gallery',
        international: 'international',
        regulations: 'regulations'
      }[this.page] || ''
    },
    isServicesPage() {
      return this.page === 'services'
    },
    isStructurePage() {
      return ['structure', 'branches', 'branch-site'].includes(this.page)
    },
    isDownloadsPage() {
      return ['downloads-center', 'public-downloads', 'party-downloads'].includes(this.page)
    },
    isPersonalPage() {
      return this.page === 'personal-center'
    },
    isConferenceDetailPage() {
      return this.page === 'conference-detail'
    },
    isNotFoundPage() {
      return this.page === 'not-found'
    },
    activeConferenceDetail() {
      if (!this.conferenceId) {
        return null
      }
      const indexMatch = this.conferenceId.match(/^local-(\d+)$/)
      if (indexMatch) {
        return this.conferences[Number(indexMatch[1])] || null
      }
      return this.conferences.find(item => String(item.conferenceId) === String(this.conferenceId)) || null
    },
    activePartyContext() {
      const matched = findPartySectionByPath(this.$route.path)
      if (!matched || !matched.section) {
        return null
      }
      const { section, child } = matched
      const layout = (child && child.layout) || section.layout || 'list'
      // 管理端固定 11 个一级 columnCode；二级事项用 category（legacyChildColumnCode 兼容旧种子）
      const columnCode = section.moduleCode
      const childCategory = child ? (child.category || child.label) : ''
      const legacyChildColumnCode = child ? child.moduleCode : null
      return {
        section,
        child,
        layout,
        columnCode,
        childCategory,
        legacyChildColumnCode,
        categories: section.categories || [],
        title: (child && child.label) || section.label,
        subtitle: (child && child.subtitle) || section.subtitle || ''
      }
    },
    partyCategoryFilter() {
      return (this.$route.query && this.$route.query.cat) || ''
    },
    activePartyTitle() {
      if (this.isPartyHub) {
        return '党建文化中心'
      }
      const ctx = this.activePartyContext
      if (ctx) {
        return ctx.title
      }
      const item = this.partyNavItems.find(section => this.isPartyNavActive(section))
      return item ? item.label : '党建文化'
    },
    activePartyEntries() {
      // 列表页改由 partyListItems 服务端分页；此处保留供专题详情等从 bootstrap 缓存解析
      const ctx = this.activePartyContext
      if (!ctx) {
        return this.dynamicCmsEntries.filter(item => item.moduleCode === 'party')
      }
      let entries
      if (this.page === 'party-downloads' || ctx.layout === 'downloads') {
        entries = this.dynamicCmsEntries.filter(item =>
          (item.moduleCode === 'downloads' && item.scope === 'party')
          || (item.moduleCode === 'party' && item.columnCode === 'party_downloads')
        )
      } else if (ctx.layout === 'directory') {
        return []
      } else {
        entries = this.dynamicCmsEntries.filter((item) => {
          if (item.moduleCode !== 'party') {
            return false
          }
          // 兼容未迁移的二级 columnCode 种子
          if (ctx.legacyChildColumnCode && item.columnCode === ctx.legacyChildColumnCode) {
            return true
          }
          if (item.columnCode !== ctx.columnCode) {
            return false
          }
          if (ctx.childCategory) {
            return item.category === ctx.childCategory
          }
          return true
        })
      }
      if (!ctx.childCategory && this.partyCategoryFilter) {
        entries = entries.filter(item => item.category === this.partyCategoryFilter)
      }
      return entries.slice().sort((a, b) => this.comparePartyArticlesByPublishTime(a, b))
    },
    activePartyTopic() {
      const id = this.$route.params && this.$route.params.entryId
      if (!id) return null
      if (this.partyTopicDetail && String(this.partyTopicDetail.entryId) === String(id)) {
        return this.partyTopicDetail
      }
      const fromPage = (this.partyListItems || []).find(item => String(item.entryId) === String(id))
      if (fromPage) return fromPage
      return this.dynamicCmsEntries.find(
        item => item.moduleCode === 'party' && item.columnCode === 'party_topics' && String(item.entryId) === String(id)
      ) || null
    },
    activePartyTopicArticles() {
      if (this.partyTopicArticles && this.partyTopicArticles.length) {
        return this.partyTopicArticles
      }
      const topic = this.activePartyTopic
      if (!topic) return []
      let ids = []
      try {
        const extra = typeof topic.extraJson === 'string' ? JSON.parse(topic.extraJson || '{}') : (topic.extraJson || {})
        ids = Array.isArray(extra.articleIds) ? extra.articleIds : []
      } catch (error) {
        ids = []
      }
      return ids
        .map(id => this.dynamicCmsEntries.find(item => String(item.entryId) === String(id)))
        .filter(Boolean)
    },
    bannerEntries() {
      return this.dynamicCmsEntries
        .filter(item => item.moduleCode === 'banners')
        .filter(item => item.entryId && item.title && (item.coverUrl || item.mediaUrl || item.fileUrl))
        .filter(item => String(item.bodyContent || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim())
        .slice(0, 10)
    },
    authButtonText() {
      return this.isLoggedIn ? '个人中心' : '登录 / 注册'
    },
    userNickname() {
      if (!this.userInfo) return '账号'
      return this.userInfo.userName || this.userInfo.nickName || this.userInfo.username || '账号'
    },
    membershipStatusCode() {
      return this.memberProfile && this.memberProfile.memberStatus ? String(this.memberProfile.memberStatus).toUpperCase() : ''
    },
    isWithdrawnMember() {
      return this.membershipStatusCode === 'WITHDRAWN'
    },
    isActiveMember() {
      return this.membershipStatusCode === 'ACTIVE'
    },
    showJoinMemberCta() {
      return !this.isLoggedIn || !this.isActiveMember
    },
    membershipStatusLabel() {
      const userType = this.userInfo && this.userInfo.userType
      if (userType === 'regular' || this.needsMembershipChoice) {
        return '请完成参与方式选择'
      }
      if (userType === 'member' && this.isActiveMember) {
        return '正式会员'
      }
      if (this.organizationBindingStatus === 'ACTIVE' && this.organizationMemberBenefit) {
        return '单位会员关联用户'
      }
      if (this.organizationBindingStatus === 'ACTIVE') {
        return '单位会员权益未生效'
      }
      if (this.organizationBindingStatus === 'PENDING') {
        return '单位会员关联待确认'
      }
      if (this.organizationBindingStatus === 'REJECTED') {
        return '单位会员关联未通过'
      }
      if (this.isExpiredMember) {
        return '会员已过期（可续费）'
      }
      if (userType === 'non_member') {
        return '非会员'
      }
      if (userType === 'member') {
        return '正式会员（办理中）'
      }
      const statusMap = {
        NON_MEMBER: '非会员',
        ACTIVE: '正式会员',
        EXPIRED: '已到期',
        WITHDRAWN: '已退会'
      }
      if (this.membershipStatusCode) {
        return statusMap[this.membershipStatusCode] || this.memberProfile.memberStatus
      }
      return (this.myPayments[0] && this.paymentStatusLabel(this.myPayments[0].paymentStatus)) || '待提交'
    },
    needsMembershipChoice() {
      if (!this.isLoggedIn || !this.userInfo) {
        return false
      }
      return this.userInfo.userType === 'regular' && String(this.userInfo.membershipChoiceMade) === '0'
    },
    organizationBindingStatus() {
      return String((this.membershipEligibility && this.membershipEligibility.organizationBindingStatus) || '').toUpperCase()
    },
    organizationMemberBenefit() {
      return Boolean(this.membershipEligibility && this.membershipEligibility.organizationMemberBenefit)
    },
    organizationMembershipName() {
      const eligibility = this.membershipEligibility || {}
      return eligibility.organizationMemberName || eligibility.organizationRequestedName || ''
    },
    organizationBindingReviewComment() {
      return (this.membershipEligibility && this.membershipEligibility.organizationBindingReviewComment) || ''
    },
    joinMemberTypeSelection() {
      if (this.applicationForm.foreignFlag) {
        return 'FOREIGN'
      }
      const category = this.applicationForm.memberCategory
      if (category === 'STUDENT' || category === 'NON_STUDENT') {
        return category
      }
      const isStudent = this.userInfo && String(this.userInfo.isStudent) === '1'
      return isStudent ? 'STUDENT' : 'NON_STUDENT'
    },
    /** 历史会员导入账号：首次登录须先修改初始密码 */
    needsForcePasswordChange() {
      if (!this.isLoggedIn || !this.userInfo) {
        return false
      }
      return this.userInfo.forceChangePassword === true
        || this.userInfo.forceChangePassword === '1'
        || this.userInfo.forceChangePassword === 1
    },
    canUpgradeToMemberPath() {
      return this.userInfo && this.userInfo.userType === 'non_member'
    },
    canBindBranch() {
      return !this.membershipEligibility || this.membershipEligibility.canBindBranch !== false
    },
    canRegisterConference() {
      return !this.membershipEligibility || this.membershipEligibility.canRegisterConference !== false
    },
    isExpiredMember() {
      return this.membershipStatusCode === 'EXPIRED'
    },
    /** 会员有效期截止日（从 memberProfile 读取） */
    memberValidEndDate() {
      const profile = this.memberProfile || {}
      return profile.validTo || profile.validEndDate || ''
    },
    /** 距到期天数（仅 ACTIVE 会员有效，过期返回负值） */
    daysUntilExpiry() {
      const end = this.memberValidEndDate
      if (!end) return null
      const endDate = new Date(end.replace(/-/g, '/'))
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
    },
    /** ACTIVE 且距到期 ≤ 30 天 */
    isNearExpiry() {
      if (!this.isActiveMember) return false
      const days = this.daysUntilExpiry
      return days !== null && days >= 0 && days <= 30
    },
    canPayMembershipFee() {
      return Boolean(this.membershipEligibility && this.membershipEligibility.canPayMembershipFee)
    },
    canRenewMembershipFee() {
      return Boolean(this.membershipEligibility && this.membershipEligibility.canRenewMembershipFee)
    },
    currentPayment() {
      return this.activeMembershipPayment
    },
    /** 前台可见的会费记录（不含已作废） */
    visibleMembershipPayments() {
      return (this.myPayments || []).filter(item => item.paymentStatus !== 'VOIDED')
    },
    /** 当前进行中的会费记录：优先在途单，否则取最近一笔已确认 */
    activeMembershipPayment() {
      if (!this.canAccessMembershipPayment) {
        return null
      }
      const list = this.visibleMembershipPayments
      if (!list.length) {
        return null
      }
      const inProgress = list.find(item => item.paymentStatus !== 'CONFIRMED')
      if (inProgress) {
        return inProgress
      }
      return list
        .filter(item => item.paymentStatus === 'CONFIRMED')
        .sort((a, b) => Number(b.paymentId) - Number(a.paymentId))[0] || null
    },
    hasInProgressMembershipPayment() {
      const payment = this.activeMembershipPayment
      return Boolean(payment && payment.paymentStatus !== 'CONFIRMED')
    },
    /** 缴费历史：仅展示已确认记录 */
    confirmedMembershipPayments() {
      return this.visibleMembershipPayments
        .filter(item => item.paymentStatus === 'CONFIRMED')
        .sort((a, b) => Number(b.paymentId) - Number(a.paymentId))
    },
    /**
     * 根据当前选中支付记录的状态自动派发可上传角色（PRD 04 §6.3）：
     * - UNPAID / VOUCHER_REJECTED → voucher（凭证阶段）
     * - INVOICE_PENDING / INVOICE_REJECTED / INVOICE_OVERDUE → invoice（发票阶段）
     * - 其他状态锁定（VOUCHER_REVIEW / INVOICE_REVIEW / CONFIRMED / VOIDED）
     */
    currentPaymentFileRole() {
      const payment = this.currentPayment
      if (!payment) return null
      const status = payment.paymentStatus
      if (status === 'UNPAID' || status === 'VOUCHER_REJECTED') return 'voucher'
      if (status === 'INVOICE_PENDING' || status === 'INVOICE_REJECTED' || status === 'INVOICE_OVERDUE') return 'invoice'
      return null
    },
    currentPaymentStageHint() {
      const payment = this.currentPayment
      if (!payment) return ''
      const role = this.currentPaymentFileRole
      if (role === 'voucher') {
        return '凭证阶段：请上传银行汇款回单/手机银行截图；提交后需等待管理员初审。'
      }
      if (role === 'invoice') {
        const deadline = payment.invoiceDeadline
        const deadlineText = deadline ? `（截止 ${this.formatDate(deadline)}）` : ''
        return `发票阶段：仅当凭证初审通过后才需要上传${deadlineText}；提交后需等待财务终审。`
      }
      switch (payment.paymentStatus) {
        case 'VOUCHER_REVIEW':
          return '凭证初审中：等待管理员审核凭证，暂不能替换或上传其他文件。'
        case 'INVOICE_REVIEW':
          return '发票终审中：等待财务审核发票，暂不能替换。'
        case 'CONFIRMED':
          return '已完成：本次会费已通过终审，可在缴费历史中查看。'
        case 'VOIDED':
          return payment.reviewComment
            ? `已作废：${payment.reviewComment}`
            : '已作废：该记录已失效，无需操作。'
        default:
          return '当前状态不允许上传文件。'
      }
    },
    membershipFeeAmount() {
      if (this.paymentFeeChoice === 'PERMANENT' && this.permanentFeeEnabled) {
        return this.permanentFeeAmount
      }
      const category = this.membershipFeeCategory
      const list = this.membershipFeeConfigs || []
      const match = list.find(item => String(item.memberCategory) === String(category))
      if (match) {
        const amount = Number(match.amount)
        if (isFinite(amount) && amount > 0) {
          return amount
        }
      }
      if (category === 'FOREIGN') {
        return 50
      }
      return 100
    },
    membershipFeeCurrencySymbol() {
      return this.membershipFeeCategory === 'FOREIGN' ? '$' : '¥'
    },
    membershipFeeCategory() {
      if (this.paymentFeeChoice === 'PERMANENT' && this.permanentFeeEnabled) {
        return 'PERMANENT'
      }
      if (this.isCouncilMemberPath) {
        return this.memberProfile.memberType
      }
      if (this.isForeignMemberPath) {
        return 'FOREIGN'
      }
      const isStudent = this.userInfo && String(this.userInfo.isStudent) === '1'
      return isStudent ? 'STUDENT' : 'NON_STUDENT'
    },
    membershipFeeCategoryLabel() {
      if (this.paymentFeeChoice === 'PERMANENT' && this.permanentFeeEnabled) {
        return '终身会员'
      }
      if (this.isCouncilMemberPath) {
        return this.memberTypeDisplayLabel
      }
      if (this.isForeignMemberPath) {
        return '外籍会员'
      }
      const isStudent = this.userInfo && String(this.userInfo.isStudent) === '1'
      return isStudent ? '学生会员' : '普通会员'
    },
    membershipFeeAmountSuffix() {
      if (this.paymentFeeChoice === 'PERMANENT') {
        return '（终身一次）'
      }
      if (this.isCouncilMemberPath) {
        return '（本届）'
      }
      return '/年'
    },
    isCouncilMemberPath() {
      const type = this.memberProfile && this.memberProfile.memberType
      return type === 'PRESIDENT'
        || type === 'VICE_PRESIDENT'
        || type === 'STANDING_DIRECTOR'
        || type === 'DIRECTOR'
    },
    isForeignMemberPath() {
      if (this.applicationForm && this.applicationForm.foreignFlag) {
        return true
      }
      const type = this.memberProfile && this.memberProfile.memberType
      return type === 'FOREIGN'
    },
    permanentFeeEnabled() {
      const list = this.membershipFeeConfigs || []
      return list.some(item => String(item.memberCategory) === 'PERMANENT')
    },
    permanentFeeAmount() {
      const list = this.membershipFeeConfigs || []
      const match = list.find(item => String(item.memberCategory) === 'PERMANENT')
      if (match) {
        const amount = Number(match.amount)
        if (isFinite(amount) && amount > 0) {
          return amount
        }
      }
      return 1000
    },
    canPayPermanentFee() {
      return Boolean(this.membershipEligibility && this.membershipEligibility.canPayPermanentFee)
    },
    canUpgradeToPermanent() {
      return Boolean(this.membershipEligibility && this.membershipEligibility.canUpgradeToPermanent)
    },
    canPayAnnualFee() {
      if (this.membershipEligibility && this.membershipEligibility.canPayAnnualFee != null) {
        return Boolean(this.membershipEligibility.canPayAnnualFee)
      }
      return this.canPayMembershipFee || this.canRenewMembershipFee
    },
    isPermanentMember() {
      return this.isActiveMember && this.memberProfile && this.memberProfile.memberType === 'PERMANENT'
    },
    memberTypeDisplayLabel() {
      const type = this.memberProfile && this.memberProfile.memberType
      const map = {
        STUDENT: '学生会员',
        NON_STUDENT: '普通会员',
        FOREIGN: '外籍会员',
        PERMANENT: '终身会员',
        PRESIDENT: '理事长',
        VICE_PRESIDENT: '副理事长',
        STANDING_DIRECTOR: '常务理事',
        DIRECTOR: '理事'
      }
      return map[type] || this.membershipStatusLabel
    },
    memberNoDisplay() {
      const profile = this.memberProfile || {}
      const user = this.userInfo || {}
      const no = profile.memberNo || user.memberNo || ''
      return String(no).trim() || '暂无'
    },
    pendingApplications() {
      return (this.myApplications || []).filter(item => item.reviewStatus === 'PENDING')
    },
    pendingApplication() {
      return this.pendingApplications[0] || null
    },
    rejectedApplication() {
      return [this.latestWithdrawApplication, this.latestJoinApplication]
        .find(item => item && item.reviewStatus === 'REJECTED') || null
    },
    latestJoinApplication() {
      return (this.myApplications || []).find(item => item.applicationType === 'JOIN') || null
    },
    latestWithdrawApplication() {
      return (this.myApplications || []).find(item => item.applicationType === 'WITHDRAW' || item.applicationType === 'QUIT') || null
    },
    joinApplicationApproved() {
      if (this.isActiveMember || this.isWithdrawnMember || this.isExpiredMember) {
        return false
      }
      return this.membershipStatusCode === 'PENDING'
    },
    canAccessMembershipPayment() {
      return this.canPayMembershipFee || this.canRenewMembershipFee || this.isActiveMember
    },
    applicationMembershipStatus() {
      const pending = this.pendingApplication
      if (pending) {
        return this.applicationTypeLabel(pending.applicationType) === '退会'
          ? 'withdrawal_submitted' : 'application_submitted'
      }
      const rejected = this.rejectedApplication
      if (rejected) {
        return this.applicationTypeLabel(rejected.applicationType) === '退会'
          ? 'withdrawal_rejected' : 'application_rejected'
      }
      return this.joinApplicationApproved ? 'application_approved' : ''
    },
    applicationMembershipStatusLabel() {
      return {
        application_submitted: '入会申请审核中',
        application_rejected: '入会申请已驳回',
        application_approved: '入会申请已通过',
        withdrawal_submitted: '退会申请审核中',
        withdrawal_rejected: '退会申请已驳回'
      }[this.applicationMembershipStatus] || ''
    },
    applicationMembershipStatusClass() {
      if (this.applicationMembershipStatus.indexOf('rejected') > -1) return 'warning'
      if (this.applicationMembershipStatus === 'application_approved') return 'success'
      return this.applicationMembershipStatus ? 'pending' : ''
    },
    canSubmitJoinApplication() {
      // PENDING 仅在「入会申请已审过」时有意义；勿仅凭档案 PENDING 阻断（误初始化会导致无法提交申请书）
      return this.isLoggedIn
        && this.userInfo && this.userInfo.userType === 'member'
        && this.membershipStatusCode !== 'ACTIVE'
        && !this.pendingApplications.some(item => item.applicationType === 'JOIN')
        && !this.joinApplicationApproved
    },
    canSubmitWithdrawApplication() {
      return this.isActiveMember
        && !this.pendingApplications.some(item => item.applicationType === 'WITHDRAW' || item.applicationType === 'QUIT')
    },
    membershipStateClass() {
      return {
        success: this.isActiveMember,
        danger: this.isWithdrawnMember || this.isExpiredMember,
        warning: !this.isActiveMember && !this.isWithdrawnMember && !this.isExpiredMember
      }
    },
    bindingServiceHint() {
      if (this.isWithdrawnMember) {
        return '您已退会，原有分会绑定关系保留，仍可以非会员身份接收会议通知并参加学术会议。'
      }
      if (this.isActiveMember) {
        return '您是学会正式会员，可按需绑定或解绑专业分会。'
      }
      return '非会员也可绑定分会，用于接收会议通知并以非会员身份报名会议。'
    },
    activeServiceProgressSteps() {
      return this.serviceProgressMap[this.activeServiceTab] || this.serviceProgressMap.main
    },
    /** 图示三流程：列表/详情=步骤1，缴费=步骤2，参会资料=步骤3（0-based） */
    conferenceProgressActiveIndex() {
      if (this.activeServiceTab !== 'conference') {
        return 0
      }
      if (this.conferenceSubView === 'attendee') {
        return 2
      }
      if (this.conferenceSubView === 'payment') {
        return 1
      }
      return 0
    },
    noticePreviewConference() {
      if (!this.noticePreviewConferenceId) {
        return null
      }
      return this.publicConferences.find(item => String(item.conferenceId) === String(this.noticePreviewConferenceId)) || null
    },
    /** 当前登录用户对应的四类费用档位 */
    currentConferenceFeeTypeKey() {
      if (!this.isLoggedIn || !this.userInfo) {
        return null
      }
      const quote = this.selectedConferenceFeeQuote
      if (quote && quote.recommendedFeeType) {
        return quote.recommendedFeeType
      }
      return this.buildRegistrationIdentity().feeType
    },
    serviceTopicContent() {
      const topicMap = {
        international: { title: '国际交流', description: '展示国际交流、合作访问和学术互访相关内容。', modules: ['international'] },
        science: { title: '科学传播', description: '展示科普活动、科学传播文章和面向公众的资料。', modules: ['science'] },
        rewards: { title: '科技奖励', description: '展示科技奖励、申报通知和获奖成果相关内容。', modules: ['tech-rewards', 'awards'] }
      }
      const topic = topicMap[this.activeServiceTab]
      if (!topic) {
        return null
      }
      return {
        ...topic,
        items: this.dynamicCmsEntries
          .filter(item => topic.modules.includes(item.moduleCode))
          .slice(0, 8)
      }
    },
    boundAssociationNames() {
      const names = this.boundActiveBranchAssociations.map(item => item.associationName)
      return names.length ? names.join('、') : '暂未绑定'
    },
    mainSocietyAssociation() {
      return this.bindableAssociations.find(item => item.associationType === 'MAIN' || item.associationId === 1) || null
    },
    branchAssociations() {
      return this.bindableAssociations.filter(item => item.associationType === 'BRANCH' || (item.associationId && item.associationId !== 1))
    },
    /** 当前仍启用且已绑定的专业分会（停用分会不计入展示数） */
    boundActiveBranchAssociations() {
      return this.branchAssociations.filter(item => this.isAssociationBound(item))
    },
    boundBranchCount() {
      return this.boundActiveBranchAssociations.length
    },
    homepagePublishSorted() {
      const publishEntries = (this.dynamicCmsEntries || []).filter(
        item => item.moduleCode === 'publish'
      )
      const preferred = preferHomepageEntries(publishEntries)
      return preferred.slice().sort((a, b) => {
        const pinDiff = Number(b.pinned === '1') - Number(a.pinned === '1')
        if (pinDiff !== 0) return pinDiff
        return String(b.publishTime || b.createTime || '').localeCompare(
          String(a.publishTime || a.createTime || '')
        )
      })
    },
    visibleNotices() {
      // 首页右侧新闻与“新闻发布”栏目统一使用 CMS publish 数据，不回退到旧 contents 表。
      return this.homepagePublishSorted
        .map(item => this.mapCmsEntryToContent(item))
        .slice(0, 3)
    },
    /** 首页左侧图文精选：优先带封面的首页发布条目，最多 3 条 */
    homepageFeaturedEntries() {
      const mapped = this.homepagePublishSorted.map(item => this.mapCmsEntryToContent(item))
      const withCover = mapped.filter(item => item.coverImageUrl || item.coverUrl)
      return (withCover.length ? withCover : mapped).slice(0, 3)
    },
    publishBoardCovers() {
      const coversEntry = this.dynamicCmsEntries.find(item =>
        item.moduleCode === 'settings' && item.columnCode === 'publish_board_covers'
      )
      if (coversEntry) {
        return getBoardCovers(coversEntry)
      }
      const siteEntry = this.dynamicCmsEntries.find(item =>
        item.moduleCode === 'settings' && item.columnCode === 'site_config'
      )
      if (siteEntry) {
        return getBoardCovers(siteEntry)
      }
      return {
        meeting_notice: '',
        important_news: '',
        society_dynamics: '',
        work_progress: ''
      }
    },
    cmsContentGroups() {
      return this.cmsSections.map(section => {
        const matched = this.combinedCmsNews.filter(item => {
          if (item.moduleCode === 'publish') {
            return item.contentType === section.type
          }
          return item.contentType === section.type || section.legacy.includes(item.contentType)
        })
        const preferred = preferHomepageEntries(matched)
        return {
          ...section,
          items: preferred.slice(0, 4)
        }
      })
    },
    cmsFeatureGroups() {
      return this.cmsFeatureModules
        .filter(module => !this.activeTopicModuleCode || module.moduleCode === this.activeTopicModuleCode)
        .map(module => ({
          ...module,
          items: this.dynamicCmsEntries
            .filter(item => item.moduleCode === module.moduleCode)
            .slice(0, 4)
        }))
    },
    combinedCmsNews() {
      const publishItems = this.dynamicCmsEntries
        .filter(item => item.moduleCode === 'publish')
        .map(item => this.mapCmsEntryToContent(item))
      // Legacy PaleoContent 作为补充；publish CMS 优先，不再混入 announcements/news/party 作为主数据
      const publishKeys = new Set(
        publishItems.map(item => `${item.contentType}|${String(item.title || '').trim()}`)
      )
      const legacyContents = this.dynamicNotices.filter(item => {
        const key = `${item.contentType || ''}|${String(item.title || '').trim()}`
        return !publishKeys.has(key)
      })
      return [...publishItems, ...legacyContents]
    },
    filteredConferenceCards() {
      let list = (this.conferences || []).filter(item => this.isConferenceVisibleInList(item))
      if (this.activeServiceBranchId) {
        list = list.filter(item => String(item.associationId) === String(this.activeServiceBranchId))
      }
      return list
    },
    filteredPublicConferences() {
      // 默认仅展示总学会 + 已绑定分会会议；未绑定分会会议不可见
      let list = (this.publicConferences || []).filter(item => this.isConferenceVisibleInList(item))
      if (this.activeServiceBranchId) {
        list = list.filter(item => String(item.associationId) === String(this.activeServiceBranchId))
      }
      return list
    },
    activeServiceBranchName() {
      if (!this.activeServiceBranchId) {
        return ''
      }
      return this.associationNameById(Number(this.activeServiceBranchId))
    },
    selectedMaterialLinks() {
      const materials = this.selectedMaterials || {}
      const labelByRole = {
        publicNoticeUrl: '会议通知',
        sealedNoticeUrl: '盖章会议通知',
        abstractTemplateUrl: '会议论文摘要模板'
      }
      return [
        { role: 'publicNoticeUrl', label: '公开通知', url: materials.publicNoticeUrl },
        { role: 'sealedNoticeUrl', label: '盖章通知', url: materials.sealedNoticeUrl },
        { role: 'abstractTemplateUrl', label: '摘要模板', url: materials.abstractTemplateUrl }
      ]
        .filter(item => item.url)
        .map(item => {
          const fromPath = this.fileNameFromUrl(item.url)
          return {
            ...item,
            fileName: fromPath || labelByRole[item.role] || '下载文件'
          }
        })
    },
    selectedRouteOptions() {
      if (this.fieldTripOnlyPost) {
        return this.availableFieldTripRoutes
      }
      const selectedPhase = this.normalizeFieldTripPhase(this.participantForm.fieldTripStage)
      if (!selectedPhase) return this.availableFieldTripRoutes
      return this.availableFieldTripRoutes.filter(route => route.phase === selectedPhase)
    },
    availableFieldTripRoutes() {
      const sex = String(this.participantForm.participantSex || '').toUpperCase()
      return this.conferenceFieldTripRoutes.filter(route => {
        if (route.genderRestriction === 'male' && sex !== 'MALE') return false
        if (route.genderRestriction === 'female' && sex !== 'FEMALE') return false
        return true
      })
    },
    fieldTripOnlyPost() {
      return this.conferenceFieldTripRoutes.length > 0 && this.conferenceFieldTripRoutes.every(route => route.phase === 'post')
    },
    fieldTripRouteGroups() {
      const labels = { pre: '会前野外', during: '会中野外', post: '会后野外' }
      return ['pre', 'during', 'post'].map(phase => ({
        phase,
        label: labels[phase],
        routes: this.availableFieldTripRoutes.filter(route => route.phase === phase)
      })).filter(group => group.routes.length)
    },
    availableAccommodationOptions() {
      return [
        { value: 'MALE_SINGLE', label: '男单间' },
        { value: 'MALE_DOUBLE', label: '男双人间' },
        { value: 'FEMALE_SINGLE', label: '女单间' },
        { value: 'FEMALE_DOUBLE', label: '女双人间' },
        { value: 'SELF', label: '自主安排' }
      ]
    },
    activeParticipantConference() {
      const registration = this.activeParticipantRegistration
      const conferenceId = registration && registration.conferenceId
      return this.publicConferences.find(item => String(item.conferenceId) === String(conferenceId))
        || this.selectedConference
        || null
    },
    activeAccommodationConfig() {
      return String((this.activeParticipantConference && this.activeParticipantConference.accommodationConfig) || '').trim()
    },
    conferenceFieldTripRoutes() {
      const conference = this.activeParticipantConference || {}
      return this.parseRouteOptions(conference.fieldTripRoutes)
    },
    hasConferenceFieldTripRoutes() {
      return this.conferenceFieldTripRoutes.length > 0
    },
    selectedSessionOptions() {
      const conference = this.activeParticipantConference || {}
      return this.parseSessionOptions(conference.sessionsConfig)
    },
    activeParticipantRegistration() {
      // 参会资料页只绑定当前场次：不回落到「第一条报名」，避免串会
      const byForm = (this.myRegistrations || []).find(
        item => String(item.registrationId) === String(this.participantForm.registrationId)
      )
      if (byForm) {
        return byForm
      }
      if (this.selectedRegistrationId) {
        return (this.myRegistrations || []).find(
          item => String(item.registrationId) === String(this.selectedRegistrationId)
        ) || null
      }
      return null
    },
    canFillAttendeeInfo() {
      return this.canFillWhitelist
    },
    canFillWhitelist() {
      const registration = this.activeParticipantRegistration
      if (!registration || registration.status === 'VOIDED'
        || (registration.frozenDueToExpiry === '1' && registration.paymentMethod !== 'OFFICIAL_CARD_ONSITE')) {
        return false
      }
      const s = registration.paymentStatus
      return s === 'INVOICE_PENDING' || s === 'INVOICE_REVIEW' || s === 'CONFIRMED'
    },
    canFillFieldTrip() {
      const registration = this.activeParticipantRegistration
      if (!registration || registration.status === 'VOIDED'
        || (registration.frozenDueToExpiry === '1' && registration.paymentMethod !== 'OFFICIAL_CARD_ONSITE')) {
        return false
      }
      return registration.paymentStatus === 'CONFIRMED'
    },
    participantLockMessage() {
      const registration = this.activeParticipantRegistration
      if (!registration) {
        return '请先完成会议报名缴费'
      }
      if (registration.status === 'VOIDED') {
        return '该会议报名已作废'
      }
      if (registration.frozenDueToExpiry === '1' && registration.paymentMethod !== 'OFFICIAL_CARD_ONSITE') {
        return '该会议报名因会员到期已冻结，请先续费会员'
      }
      if (!this.canFillWhitelist) {
        if (registration.paymentStatus === 'INVOICE_OVERDUE') {
          return '发票已逾期，请先上传电子发票后再填写参会资料'
        }
        return '会议费凭证审核通过后可先填写参会基础信息、摘要与住宿；野外与盖章材料需发票终审通过'
      }
      return ''
    },
    myRegistrationSummaries() {
      return this.myRegistrations.map(item => ({
        registrationId: item.registrationId,
        conferenceId: item.conferenceId,
        conferenceTitle: this.conferenceTitleById(item.conferenceId),
        statusLabel: this.registrationStatusLabel(item),
        feeAmount: item.feeAmount,
        frozen: item.frozenDueToExpiry === '1',
        voided: item.status === 'VOIDED',
        needsRenewal: item.frozenDueToExpiry === '1'
      }))
    },
    /** 前台可见的会议报名（不含已作废），供缴费流切换 */
    visibleRegistrations() {
      return (this.myRegistrations || []).filter(item => item.status !== 'VOIDED')
    },
    /** 缴费流当前聚焦的报名记录：优先用户显式选择（且与当前会议一致），否则按会议匹配；当前会议无报名时不回落到其他会议 */
    selectedRegistration() {
      const list = this.visibleRegistrations
      if (!list.length) {
        return null
      }
      const chosen = list.find(item => String(item.registrationId) === String(this.selectedRegistrationId))
      if (chosen) {
        if (!this.selectedConference
          || String(chosen.conferenceId) === String(this.selectedConference.conferenceId)) {
          return chosen
        }
      }
      if (this.selectedConference) {
        return list.find(
          item => String(item.conferenceId) === String(this.selectedConference.conferenceId)
        ) || null
      }
      const inProgress = list.find(item => item.paymentStatus !== 'CONFIRMED')
      return inProgress || list[0]
    },
    /** 可新报名的会议：排除已存在有效报名的会议（一场一缴） */
    registrableConferences() {
      const registeredIds = new Set(this.visibleRegistrations.map(item => String(item.conferenceId)))
      return this.filteredPublicConferences.filter(item => !registeredIds.has(String(item.conferenceId)))
    },
    /**
     * 会议费当前可上传角色（PRD 06 §6.4 两阶段）：
     * - UNPAID / VOUCHER_REJECTED → voucher
     * - INVOICE_PENDING / INVOICE_REJECTED / INVOICE_OVERDUE → invoice
     * - 其余状态锁定（审核中 / 已确认 / 冻结）
     */
    conferencePaymentFileRole() {
      const registration = this.selectedRegistration
      if (!registration || registration.frozenDueToExpiry === '1') {
        return null
      }
      const status = registration.paymentStatus
      if (['UNPAID', 'VOUCHER_REJECTED'].includes(status)) {
        return 'voucher'
      }
      if (['INVOICE_PENDING', 'INVOICE_REJECTED', 'INVOICE_OVERDUE'].includes(status)) {
        return 'invoice'
      }
      return null
    },
    groupedPaymentHistory() {
      return this.paymentHistoryEntries
    },
    /** 缴费流水：会员费 + 会议费逐笔，按时间倒序 */
    paymentHistoryEntries() {
      const entries = []
      ;(this.myPayments || []).forEach(item => {
        if (item.paymentStatus === 'VOIDED') {
          return
        }
        const paidAt = item.updateTime || item.createTime || ''
        const validEndDate = item.validEndDate || ''
        const isPermanent = String(item.memberCategory || '').toUpperCase() === 'PERMANENT'
        entries.push({
          key: `membership-${item.paymentId || paidAt}`,
          kind: 'membership',
          associationName: '中国古生物学会',
          title: this.membershipCategoryLabel(item.memberCategory),
          subtitle: '会员费',
          amount: Number(item.amount || 0),
          currencySymbol: String(item.memberCategory || '').toUpperCase() === 'FOREIGN' ? '$' : '¥',
          statusLabel: this.paymentStatusLabel(item.paymentStatus),
          paidAt,
          paidAtLabel: this.formatDateTime(paidAt),
          validEndDate,
          validEndDateLabel: isPermanent ? '' : (validEndDate ? this.formatDate(validEndDate) : ''),
          validityLabel: isPermanent ? '长期有效' : '',
          voucherUrl: item.voucherUrl || '',
          invoiceUrl: item.invoiceUrl || ''
        })
      })
      ;(this.myRegistrations || []).forEach(item => {
        if (item.status === 'VOIDED' || item.paymentStatus === 'VOIDED') {
          return
        }
        const paidAt = item.updateTime || item.createTime || ''
        const conferenceTitle = item.conferenceTitle
          || this.conferenceTitleById(item.conferenceId)
        entries.push({
          key: `conference-${item.registrationId || item.conferenceId || paidAt}`,
          kind: 'conference',
          associationName: this.associationNameById(item.associationId),
          title: conferenceTitle,
          subtitle: '会议费',
          amount: Number(item.feeAmount || 0),
          currencySymbol: '¥',
          statusLabel: this.registrationStatusLabel(item),
          paidAt,
          paidAtLabel: this.formatDateTime(paidAt),
          conferenceId: item.conferenceId,
          voucherUrl: item.voucherUrl || '',
          invoiceUrl: item.invoiceUrl || ''
        })
      })
      return entries.sort((a, b) => String(b.paidAt || '').localeCompare(String(a.paidAt || '')))
    }
  },
  mounted() {
    setUnauthorizedHandler(() => {
      this.isLoggedIn = false
      this.userInfo = null
      this.memberProfile = null
      this.myNotifications = []
      this.unreadNotificationCount = 0
      this.notificationsSynced = true
      this.accountDropdownOpen = false
      this.authMode = 'login'
      this.authDialogOpen = true
      this.showAuthFeedback('登录已过期，请重新登录')
    })
    this.syncServiceTabFromRoute()
    this.setupSectionObserver()
    this.loadPublicPageData().then(() => {
      this.loadRoutedContent()
      this.loadActiveChannel()
      this.loadPartyListPage()
    })
    this.loadSelfService()
    document.addEventListener('click', this.handleGlobalClick)
    window.addEventListener('focus', this.refreshSiteConfig)
    document.addEventListener('visibilitychange', this.handleSiteConfigVisibility)
    this.siteConfigTimer = setInterval(this.refreshSiteConfig, 30000)
  },
  watch: {
    '$route'(to, from) {
      const pathChanged = !from || to.path !== from.path
      if (pathChanged) {
        this.partyListPageNum = 1
      }
      const pageChanged = !from || (to.meta.page !== from.meta.page)
      const paramsChanged = !from || JSON.stringify(to.params) !== JSON.stringify(from.params)
      const queryCatChanged = !from || (to.query && to.query.cat) !== (from.query && from.query.cat)
      // 组织生活/党员队伍建设等同 page 下子路径切换（换届选举 ↔ 三会一课）必须重拉列表
      if (pageChanged || paramsChanged || queryCatChanged || pathChanged) {
        this.selectedContent = null
        this.selectedCmsEntry = null
        this.contentDetailLoading = false
        this.contentDetailError = ''
        this.mobileNavOpen = false
        this.loadActiveChannel()
        this.loadRoutedContent()
        if (queryCatChanged && !pathChanged) {
          this.partyListPageNum = 1
        }
        if (pathChanged) {
          this.partyListItems = []
          this.partyListTotal = 0
        }
        this.loadPartyListPage()
        this.$nextTick(() => this.setupSectionObserver())
      }
      this.syncServiceTabFromRoute()
    },
    '$route.query.tab'() {
      this.syncServiceTabFromRoute()
    },
    authMode() {
      this.resetAuthVerification()
      this.forgotCodeSending = false
      if (!this.authFeedbackDialogOpen) {
        this.authFeedback = ''
      }
    }
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect()
    }
    if (this.bannerTimer) {
      clearInterval(this.bannerTimer)
    }
    if (this.emailCodeTimer) {
      clearInterval(this.emailCodeTimer)
    }
    if (this.siteConfigTimer) {
      clearInterval(this.siteConfigTimer)
    }
    document.removeEventListener('click', this.handleGlobalClick)
    window.removeEventListener('focus', this.refreshSiteConfig)
    document.removeEventListener('visibilitychange', this.handleSiteConfigVisibility)
  },
  methods: {
    handleSiteConfigVisibility() {
      if (document.visibilityState === 'visible') {
        this.refreshSiteConfig()
      }
    },
    async refreshSiteConfig() {
      if (this.siteConfigRefreshPending) return
      this.siteConfigRefreshPending = true
      try {
        const result = await listPublicCmsEntries({ moduleCode: 'settings', pageNum: 1, pageSize: 50 })
        this.applySiteConfig(unwrapPublicPage(result).rows.map(item => this.mapCmsEntry(item)))
      } catch (error) {
        // 主题同步失败不影响浏览；下次聚焦或定时检查会重试。
      } finally {
        this.siteConfigRefreshPending = false
      }
    },
    pickDefaultPaymentSelection() {
      const payment = this.activeMembershipPayment
      if (payment && payment.paymentId != null) {
        this.uploadSelection.paymentId = payment.paymentId
      }
    },
    setupSectionObserver() {
      if (this.observer) {
        this.observer.disconnect()
      }
      this.observer = new IntersectionObserver(this.handleIntersect, {
        rootMargin: '-35% 0px -55% 0px',
        threshold: 0
      })
      document.querySelectorAll('.section-observed').forEach(section => {
        this.observer.observe(section)
      })
    },
    handleGlobalClick() {
      this.notificationOpen = false
      this.accountDropdownOpen = false
    },
    channelCodeForPage(channel) {
      if (!channel) {
        return ''
      }
      const code = channel.channelCode || ''
      if (code.indexOf('party-') === 0) {
        const partyCode = code.replace('party-', '')
        return partyCode === 'downloads' ? 'party-downloads' : partyCode
      }
      return code
    },
    pageMeta(page) {
      return this.channels.find(item => this.channelCodeForPage(item) === page) || {}
    },
    routeToPage(path) {
      return {
        '/announcements': 'announcements',
        '/organizations': 'organizations',
        '/committees': 'committees',
        '/work': 'work',
        '/activities': 'activities',
        '/team-building': 'team-building',
        '/theory-study': 'theory-study',
        '/dynamics': 'dynamics',
        '/special-topics': 'special-topics',
        '/exemplars': 'exemplars',
        '/reporting': 'reporting',
        '/downloads': 'party-downloads'
      }[path] || ''
    },
    staticPageTitle(page) {
      const matchedNav = this.navItems.find(item => item.id === page)
      if (matchedNav) {
        return matchedNav.label
      }
      const matchedParty = this.partySections.find(item => this.routeToPage(item.path) === page)
      if (matchedParty) {
        return matchedParty.label
      }
      return {
        news: '新闻动态',
        'news-publish': '通知要闻',
        'society-announcements': '会员公告',
        publications: '出版物',
        'society-dynamics': '学会动态',
        'work-progress': '工作进展',
        'branch-site': '分会子站',
        'public-downloads': '公开文件',
        'downloads-center': '资料下载',
        'personal-center': '个人中心',
        'conference-detail': '会议详情',
        'content-detail': '内容详情',
        'not-found': '页面不存在'
      }[page] || '中国古生物学会'
    },
    async loadActiveChannel() {
      if (!this.$route || !this.$route.path || this.isContentDetailPage || this.isConferenceDetailPage) {
        this.activeBlocks = []
        return
      }
      try {
        const result = await getPublicChannel({ routePath: this.$route.path })
        this.activeBlocks = (result.data && result.data.blocks) || []
      } catch (error) {
        this.activeBlocks = []
      }
    },
    associationNameById(associationId) {
      const association = this.bindableAssociations.find(item => item.associationId === associationId)
      return association ? association.associationName : `学会/分会 ${associationId || '未知'}`
    },
    handleIntersect(entries) {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.activeSection = entry.target.id
          if (entry.target.id === 'topics' && !this.activeTopicModule) {
            this.activeTopicModule = 'timeline'
          }
        }
      })
    },
    isNavActive(item) {
      return this.$route.path === item.path
    },
    navigateNav(item) {
      this.goPath(item.path || '/')
    },
    isPartyNavActive(item) {
      const current = this.$route.path || ''
      if (current === item.path) return true
      return current.startsWith(item.path + '/')
    },
    shouldLoadPartyListPage() {
      if (this.isPartyHub) return false
      const ctx = this.activePartyContext
      if (!ctx) return false
      if (ctx.layout === 'directory' || ctx.layout === 'reporting') return false
      return true
    },
    comparePartyArticlesByPublishTime(a, b) {
      const pinnedValues = ['1', 'Y', true, 'true']
      const pinDiff = Number(pinnedValues.includes(b && b.pinned)) - Number(pinnedValues.includes(a && a.pinned))
      if (pinDiff !== 0) return pinDiff
      return String((b && (b.publishTime || b.date || b.createTime)) || '').localeCompare(
        String((a && (a.publishTime || a.date || a.createTime)) || '')
      )
    },
    sortPartyArticlesByPublishTime(list) {
      return (list || []).slice().sort((a, b) => this.comparePartyArticlesByPublishTime(a, b))
    },
    async loadPartyListPage() {
      if (!this.shouldLoadPartyListPage()) {
        this.partyListItems = []
        this.partyListTotal = 0
        this.partyTopicDetail = null
        this.partyTopicArticles = []
        return
      }
      const ctx = this.activePartyContext
      const seq = ++this.partyListLoadSeq
      try {
        if (ctx.layout === 'topics' && this.$route.params && this.$route.params.entryId) {
          const entryId = this.$route.params.entryId
          const result = await getPublicCmsEntry(entryId)
          if (seq !== this.partyListLoadSeq) return
          const topic = this.mapCmsEntry((result && result.data) || result || {})
          this.partyTopicDetail = topic
          this.partyListItems = topic && topic.entryId ? [topic] : []
          this.partyListTotal = this.partyListItems.length
          let ids = []
          try {
            const extra = typeof topic.extraJson === 'string' ? JSON.parse(topic.extraJson || '{}') : (topic.extraJson || {})
            ids = Array.isArray(extra.articleIds) ? extra.articleIds : []
          } catch (error) {
            ids = []
          }
          if (ids.length) {
            const articles = await Promise.all(ids.map(id => getPublicCmsEntry(id).catch(() => null)))
            if (seq !== this.partyListLoadSeq) return
            this.partyTopicArticles = articles
              .map(item => item && this.mapCmsEntry(item.data || item))
              .filter(item => item && item.entryId)
          } else {
            this.partyTopicArticles = []
          }
          return
        }

        this.partyTopicDetail = null
        this.partyTopicArticles = []
        let result
        const category = (ctx.layout === 'downloads' || this.page === 'party-downloads')
          ? undefined
          : (ctx.childCategory || this.partyCategoryFilter || undefined)
        if (ctx.layout === 'downloads' || this.page === 'party-downloads') {
          result = await listPublicCmsEntries({
            moduleCode: 'downloads',
            scope: 'party',
            pageNum: this.partyListPageNum,
            pageSize: this.partyListPageSize
          })
        } else {
          result = await listPublicCmsEntries({
            moduleCode: 'party',
            columnCode: ctx.columnCode || undefined,
            category: category || undefined,
            pageNum: this.partyListPageNum,
            pageSize: this.partyListPageSize
          })
        }
        if (seq !== this.partyListLoadSeq) return
        const { rows, total } = unwrapPublicPage(result)
        let mapped = rows.map(item => this.mapCmsEntry(item))
        mapped = category
          ? mapped.filter(item => item.category === category
            || (ctx.legacyChildColumnCode && item.columnCode === ctx.legacyChildColumnCode))
          : mapped
        if (!(ctx.layout === 'downloads' || this.page === 'party-downloads')) {
          mapped = this.sortPartyArticlesByPublishTime(mapped)
        }
        const paged = applyClientPageFallback(mapped, total, this.partyListPageNum, this.partyListPageSize)
        this.partyListItems = paged.rows
        this.partyListTotal = paged.total
      } catch (error) {
        if (seq !== this.partyListLoadSeq) return
        if (!this.partyListItems.length) {
          this.partyListItems = []
          this.partyListTotal = 0
        }
      }
    },
    changePartyListPage(page) {
      const next = Number(page)
      if (!Number.isFinite(next) || next < 1 || next === this.partyListPageNum) return
      this.partyListPageNum = next
      this.loadPartyListPage()
    },
    setPartyCategory(cat) {
      const query = { ...this.$route.query }
      if (cat) {
        query.cat = cat
      } else {
        delete query.cat
      }
      this.$router.replace({ path: this.$route.path, query }).catch(() => {})
    },
    openPartyEntry(item) {
      const linkUrl = String(item && item.linkUrl || '').trim()
      if (/^https?:\/\//i.test(linkUrl)) {
        window.open(linkUrl, '_blank', 'noopener')
        return
      }
      this.openCmsEntry(item)
    },
    goPath(path) {
      if (!path) {
        return
      }
      if (/^https?:\/\//.test(path)) {
        window.open(path, '_blank')
        return
      }
      const raw = path.startsWith('/') ? path : `/${path.replace(/^#/, '')}`
      const hashIndex = raw.indexOf('#')
      const withoutHash = hashIndex >= 0 ? raw.slice(0, hashIndex) : raw
      const hash = hashIndex >= 0 ? raw.slice(hashIndex) : ''
      const qIndex = withoutHash.indexOf('?')
      const pathname = qIndex >= 0 ? withoutHash.slice(0, qIndex) : withoutHash
      const search = qIndex >= 0 ? withoutHash.slice(qIndex + 1) : ''
      const query = {}
      if (search) {
        search.split('&').forEach(pair => {
          if (!pair) return
          const eq = pair.indexOf('=')
          const key = decodeURIComponent(eq >= 0 ? pair.slice(0, eq) : pair)
          const val = decodeURIComponent(eq >= 0 ? pair.slice(eq + 1) : '')
          if (key) query[key] = val
        })
      }
      const next = { path: pathname }
      if (Object.keys(query).length) next.query = query
      if (hash) next.hash = hash.replace(/^#/, '')
      const samePath = this.$route.path === pathname
      const sameQuery = JSON.stringify(this.$route.query || {}) === JSON.stringify(next.query || {})
      const sameHash = (this.$route.hash || '') === (hash || '')
      if (!samePath || !sameQuery || !sameHash) {
        this.$router.push(next).catch(() => {})
      }
    },
    goServicesTab(tab, options = {}) {
      const normalizedTab = this.normalizeServiceTab(tab)
      if (normalizedTab !== 'conference') {
        this.conferenceSubView = 'list'
      } else if (options.resetView !== false && !options.keepView) {
        this.conferenceSubView = 'list'
      }
      this.activeServiceTab = normalizedTab
      if (Object.prototype.hasOwnProperty.call(options, 'branch')) {
        this.activeServiceBranchId = options.branch ? String(options.branch) : ''
        this.selectedConference = null
        this.selectedMaterials = {}
      }
      const query = { tab: normalizedTab }
      if (normalizedTab === 'conference' && this.activeServiceBranchId) {
        query.branch = this.activeServiceBranchId
      }
      if (normalizedTab === 'science' && options.cat) {
        query.cat = String(options.cat)
      }
      const sameCat = (this.$route.query.cat || '') === (query.cat || '')
      if (this.$route.path !== '/services'
        || this.$route.query.tab !== query.tab
        || (this.$route.query.branch || '') !== (query.branch || '')
        || !sameCat) {
        this.$router.push({ path: '/services', query })
      }
    },
    normalizeServiceTab(tab) {
      const aliases = {
        binding: 'branches',
        membership: 'member',
        payment: 'member',
        materials: 'conference'
      }
      const value = aliases[tab] || tab || 'main'
      return this.serviceTabs.some(item => item.value === value) ? value : 'main'
    },
    syncServiceTabFromRoute() {
      if (!this.isServicesPage) {
        return
      }
      this.activeServiceTab = this.normalizeServiceTab(this.$route.query.tab)
      const branchId = this.activeServiceTab === 'conference' ? String(this.$route.query.branch || '') : ''
      if (branchId !== this.activeServiceBranchId) {
        this.activeServiceBranchId = branchId
        this.selectedConference = null
        this.selectedMaterials = {}
        this.selectConferenceFromServiceBranch()
      }
    },
    openForgotPassword() {
      this.forgotPasswordEmail = this.authForm.account || this.forgotPasswordEmail
      this.authMode = 'forgot'
      this.authFeedback = ''
    },
    async sendForgotPasswordCode() {
      const email = (this.forgotPasswordEmail || '').trim().toLowerCase()
      if (!this.isValidEmail(email)) {
        this.showAuthFeedback('请填写正确的邮箱')
        return
      }
      if (this.forgotCodeSending || this.forgotCodeSeconds > 0) {
        return
      }
      this.forgotCodeSending = true
      try {
        await sendEmailCode(email, 'RESET_PASSWORD')
        this.showAuthFeedback('若该邮箱已注册，验证码已发送至邮箱，请查收')
        this.startForgotCodeCountdown()
      } catch (error) {
        this.showAuthFeedback(error.message || '验证码发送失败')
      } finally {
        this.forgotCodeSending = false
      }
    },
    startForgotCodeCountdown() {
      this.forgotCodeSeconds = 60
      if (this.forgotCodeTimer) {
        clearInterval(this.forgotCodeTimer)
      }
      this.forgotCodeTimer = setInterval(() => {
        this.forgotCodeSeconds -= 1
        if (this.forgotCodeSeconds <= 0) {
          clearInterval(this.forgotCodeTimer)
          this.forgotCodeTimer = null
          this.forgotCodeSeconds = 0
        }
      }, 1000)
    },
    scrollTo(id) {
      const target = document.getElementById(id)
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    toggleAccountDropdown() {
      this.accountDropdownOpen = !this.accountDropdownOpen
    },
    openAccountCenter() {
      this.accountDropdownOpen = false
      this.goPath('/personal-center')
    },
    openAuth(mode) {
      this.authMode = mode
      this.authDialogOpen = true
      this.authFeedback = ''
      this.authFeedbackDialogOpen = false
      this.resetAuthVerification()
    },
    async startCstCloudLogin() {
      if (this.cstCloudStarting) return
      this.cstCloudStarting = true
      this.authFeedback = ''
      try {
        const result = await startCstCloudLoginApi(currentRelativePath(this.$route))
        const authorizationUrl = getAuthorizationUrl(result)
        if (!isSafeAuthorizationUrl(authorizationUrl)) {
          throw new Error('科技云授权地址无效，请稍后重试')
        }
        window.location.assign(authorizationUrl)
      } catch (error) {
        const message = error.message || '暂时无法连接中国科技云，请使用邮箱密码登录'
        if (/中国科技云.*(?:暂未启用|未启用)/.test(message)) {
          this.authFeedback = ''
          this.cstCloudUnavailableMessage = '中国科技云登录功能暂未启用，请先使用邮箱密码登录。'
          this.cstCloudUnavailableOpen = true
        } else {
          this.showAuthFeedback(message)
        }
        this.cstCloudStarting = false
      }
    },
    closeCstCloudUnavailable() {
      this.cstCloudUnavailableOpen = false
      this.cstCloudUnavailableMessage = ''
    },
    showAuthFeedback(message) {
      this.authFeedback = message || '操作失败，请稍后重试'
      this.authFeedbackDialogOpen = true
    },
    closeAuthFeedback() {
      this.authFeedbackDialogOpen = false
    },
    openContactUs() {
      this.contactUsOpen = true
    },
    closeContactUs() {
      this.contactUsOpen = false
    },
    async loadSelfService() {
      if (!this.isLoggedIn || !localStorage.getItem('paleo_user_token')) {
        this.isLoggedIn = false
        this.selfServiceLoading = false
        this.membershipSessionSynced = true
        this.notificationsSynced = true
        clearSelfServiceSnapshot()
        return
      }
      // 同步完成前不渲染路径相关 UI，避免刷新闪过「同步中 / 办理中 / 入会向导」等中间态
      this.membershipSessionSynced = false
      this.selfServiceLoading = true
      this.notificationsSynced = false
      // 丢弃快照里的申请/缴费，防止与最新身份短暂错配
      this.myApplications = []
      this.myPayments = []
      try {
        const userProfile = await getUserProfile()
        const session = userProfile.data || userProfile || {}
        this.applyMembershipSession(session)
        this.userInfo = session.user || this.userInfo
        this.memberProfile = session.profile || this.memberProfile
        this.membershipEligibility = session.eligibility || this.membershipEligibility
        if (this.userInfo) {
          this.applicationForm.applicantName = this.userInfo.userName || ''
        }
        if (this.needsForcePasswordChange) {
          this.syncProfileForm(this.userInfo || {})
          this.membershipSessionSynced = true
          this.selfServiceLoading = false
          this.notificationsSynced = true
          this.ensureForcePasswordChanged()
          return
        }

        // 关键路径：身份状态 + 申请/缴费/档案一并完成后再放行首屏
        // getMembershipStatus 可能校正 userType，必须进关键路径，否则会先闪「正式会员(办理中)」再跳非会员
        const [applications, payments, profile, membershipStatus] = await Promise.all([
          listMyApplications(),
          listMyPayments(),
          getMyMemberProfile(),
          getMembershipStatus().catch(() => null)
        ])
        this.myApplications = applications.data || []
        this.myPayments = payments.data || []
        this.pickDefaultPaymentSelection()
        this.memberProfile = profile.data || this.memberProfile
        if (membershipStatus) {
          const statusData = membershipStatus.data || membershipStatus || {}
          this.applyMembershipSession(statusData)
          if (statusData.user) {
            this.userInfo = statusData.user
          }
        }
        await Promise.all([
          this.loadMembershipBankAccount(),
          this.loadMembershipFeeConfig()
        ])
        this.syncProfileForm(this.userInfo || {})
        this.syncApplicationMemberCategory()
        if (!this.paymentForm.applicationId && this.myApplications[0]) {
          this.paymentForm.applicationId = this.myApplications[0].applicationId
        }
        if (!this.uploadSelection.applicationId && this.myApplications[0]) {
          this.uploadSelection.applicationId = this.myApplications[0].applicationId
        }
        if (!this.uploadSelection.paymentId) {
          this.pickDefaultPaymentSelection()
        }
        this.selfServiceStatus = ''
        this.membershipSessionSynced = true
        this.selfServiceLoading = false
        this.persistSelfServiceCache()

        // 非关键：分会/会议/通知/模板等后台补齐，不挡首屏
        this.loadSelfServiceSecondary()
      } catch (error) {
        // 鉴权失败时 request 层已清 Token；此处兜底避免假登录壳层残留
        if (!localStorage.getItem('paleo_user_token')) {
          this.isLoggedIn = false
          this.userInfo = null
          this.memberProfile = null
          clearSelfServiceSnapshot()
        }
        this.selfServiceStatus = error.message || '未登录或接口暂不可用'
        this.selfServiceLoading = false
        this.membershipSessionSynced = true
        this.notificationsSynced = true
      }
    },
    async loadSelfServiceSecondary() {
      if (!this.isLoggedIn || !localStorage.getItem('paleo_user_token')) {
        this.notificationsSynced = true
        return
      }
      try {
        const [associations, bindings, registrations, contents, notifications] = await Promise.all([
          listBindableAssociations(),
          listMyBindings(),
          listMyRegistrations(),
          listMyContents(),
          listMyNotifications({ limit: 50 }).catch(() => ({ data: { notifications: [], unreadCount: 0 } }))
        ])
        this.bindableAssociations = (associations.data || []).map(item => this.normalizeAssociation(item))
        this.boundBranchCodes = Array.isArray(bindings.data) ? bindings.data : []
        this.syncBindingState()
        this.myRegistrations = registrations.data || []
        const notificationData = notifications.data || notifications || {}
        this.myNotifications = notificationData.notifications || []
        this.unreadNotificationCount = notificationData.unreadCount || 0
        this.myContents = (contents.data || []).map(item => this.mapContentItem(item))
        if (!this.participantForm.registrationId && this.myRegistrations[0]) {
          this.participantForm.registrationId = this.myRegistrations[0].registrationId
        }
        this.syncParticipantFormFromRegistration()
        if (!this.uploadSelection.registrationId && this.myRegistrations[0]) {
          this.uploadSelection.registrationId = this.myRegistrations[0].registrationId
        }
        try {
          const statusResult = await getMembershipStatus()
          const statusData = statusResult.data || statusResult
          // 身份已在关键路径用 status 校正；次要加载仅刷新资格，避免再次改写 userType 造成闪页
          if (statusData.eligibility) {
            this.membershipEligibility = statusData.eligibility
          }
        } catch (error) {
          // keep profile/info fallback when status endpoint unavailable
        }
        await Promise.all([
          this.loadMembershipTemplates(),
          this.loadPublicConferences()
        ])
        this.persistSelfServiceCache()
      } catch (error) {
        // 次要数据失败不回退首屏；用户刷新或切 Tab 时会再拉
      } finally {
        this.notificationsSynced = true
      }
    },
    persistSelfServiceCache() {
      if (!this.isLoggedIn || !this.userInfo) {
        return
      }
      try {
        sessionStorage.setItem(SELF_SERVICE_CACHE_KEY, JSON.stringify({
          userInfo: this.userInfo,
          memberProfile: this.memberProfile,
          membershipEligibility: this.membershipEligibility,
          myApplications: this.myApplications,
          myPayments: this.myPayments,
          membershipBankAccount: this.membershipBankAccount,
          membershipFeeConfigs: this.membershipFeeConfigs,
          membershipTemplates: this.membershipTemplates,
          boundBranchCodes: this.boundBranchCodes,
          savedAt: Date.now()
        }))
      } catch (error) {
        // ignore quota / private mode
      }
    },
    async loadPublicPageData() {
      try {
        // CMS：仅拉站点配置 + 首页预览所需模块（非全站语料）；列表页各自按页请求
        const [
          associations,
          contents,
          settingsRes,
          bannersRes,
          publishRes,
          mediaRes,
          galleryRes,
          conferenceDetails,
          channels
        ] = await Promise.all([
          listPublicAssociations(),
          listPublicContents(),
          listPublicCmsEntries({ moduleCode: 'settings', pageNum: 1, pageSize: 50 }).catch(() => ({ rows: [], total: 0 })),
          listPublicCmsEntries({ moduleCode: 'banners', pageNum: 1, pageSize: 20 }).catch(() => ({ rows: [], total: 0 })),
          listPublicCmsEntries({ moduleCode: 'publish', pageNum: 1, pageSize: 6 }).catch(() => ({ rows: [], total: 0 })),
          listPublicCmsEntries({ moduleCode: 'media', pageNum: 1, pageSize: 20 }).catch(() => ({ rows: [], total: 0 })),
          listPublicCmsEntries({ moduleCode: 'gallery', scope: 'society', pageNum: 1, pageSize: 500 }).catch(() => ({ rows: [], total: 0 })),
          listOpenConferences(),
          listPublicChannels()
        ])
        this.publicPageError = ''
        const associationList = associations.data || []
        if (associationList.length) {
          this.bindableAssociations = associationList
          this.branches = associationList.map(item => item.associationName)
        }
        const contentList = contents.data || []
        if (contentList.length) {
          this.dynamicNotices = contentList.slice(0, 12).map(item => this.mapContentItem(item))
        }
        this.dynamicCmsEntries = [settingsRes, bannersRes, publishRes, mediaRes, galleryRes]
          .flatMap(r => unwrapPublicPage(r).rows)
          .map(item => this.mapCmsEntry(item))
        this.applySiteConfig(this.dynamicCmsEntries)
        this.startBannerCarousel()
        const conferenceList = conferenceDetails.data || []
        if (conferenceList.length) {
          this.conferences = conferenceList.map(item => this.mapConferenceCard(item))
        }
        this.channels = channels.data || []
        // 公开下载：pageSize=20，与列表页同一套 loadPublicDownloadsPage
        this.publicFilesPageNum = 1
        await this.loadPublicDownloadsPage()
      } catch (error) {
        this.publicPageError = error.message || '请求失败'
      }
    },
    startBannerCarousel() {
      if (this.bannerTimer) {
        clearInterval(this.bannerTimer)
        this.bannerTimer = null
      }
      if (this.bannerEntries.length <= 1) {
        this.activeBannerIndex = 0
        return
      }
      this.bannerTimer = setInterval(() => {
        this.activeBannerIndex = (this.activeBannerIndex + 1) % this.bannerEntries.length
      }, 10000)
    },
    async sendRegisterEmailCode() {
      const account = (this.authForm.account || '').trim().toLowerCase()
      if (!this.isValidEmail(account)) {
        this.showAuthFeedback('请填写正确的邮箱')
        this.registerEmailCodeReady = false
        this.registerEmailCodeEmail = ''
        return
      }
      if (this.emailCodeSending || this.emailCodeSeconds > 0) {
        return
      }
      this.emailCodeSending = true
      try {
        await sendEmailCode(account, 'REGISTER')
        this.registerEmailCodeReady = true
        this.registerEmailCodeEmail = account
        this.showAuthFeedback('验证码已发送至邮箱，请查收')
        this.startEmailCodeCountdown()
      } catch (error) {
        this.showAuthFeedback(error.message || '验证码发送失败')
        this.registerEmailCodeReady = false
        this.registerEmailCodeEmail = ''
      } finally {
        this.emailCodeSending = false
      }
    },
    startEmailCodeCountdown() {
      this.emailCodeSeconds = 60
      if (this.emailCodeTimer) {
        clearInterval(this.emailCodeTimer)
      }
      this.emailCodeTimer = setInterval(() => {
        this.emailCodeSeconds -= 1
        if (this.emailCodeSeconds <= 0) {
          clearInterval(this.emailCodeTimer)
          this.emailCodeTimer = null
          this.emailCodeSeconds = 0
        }
      }, 1000)
    },
    resetAuthVerification() {
      this.authForm.emailOtp = ''
      this.registerEmailCodeReady = false
      this.registerEmailCodeEmail = ''
      this.emailCodeSending = false
      if (this.emailCodeTimer) {
        clearInterval(this.emailCodeTimer)
        this.emailCodeTimer = null
      }
      this.emailCodeSeconds = 0
    },
    async submitAuth() {
      try {
        this.authSubmitting = true
        if (this.authMode === 'forgot') {
          const email = (this.forgotPasswordEmail || '').trim().toLowerCase()
          if (!this.isValidEmail(email)) {
            this.showAuthFeedback('请填写正确的邮箱')
            return
          }
          if (!/^\d{6}$/.test((this.forgotPasswordCode || '').trim())) {
            this.showAuthFeedback('请输入6位邮箱验证码')
            return
          }
          if (this.forgotPasswordNew !== this.forgotPasswordConfirm) {
            this.showAuthFeedback('两次输入的新密码不一致')
            return
          }
          const resetMsg = validatePasswordPolicy(this.forgotPasswordNew, email)
          if (resetMsg) {
            this.showAuthFeedback(resetMsg)
            return
          }
          await forgotPassword({
            email,
            verificationCode: this.forgotPasswordCode.trim(),
            newPassword: this.forgotPasswordNew,
            confirmPassword: this.forgotPasswordConfirm
          })
          clearToken()
          this.isLoggedIn = false
          this.authMode = 'login'
          this.showAuthFeedback('密码已重置，请重新登录')
          return
        }

        const account = (this.authForm.account || '').trim()
        if (!account || !this.authForm.password) {
          this.showAuthFeedback('请填写账号和密码')
          return
        }
        if (!this.authForm.slideTicket) {
          this.showAuthFeedback('请先完成滑动验证')
          return
        }
        if (this.authMode === 'register') {
          if (this.authForm.password !== this.authForm.confirmPassword) {
            this.showAuthFeedback('两次输入的密码不一致')
            return
          }
          const policyMsg = validatePasswordPolicy(this.authForm.password, account)
          if (policyMsg) {
            this.showAuthFeedback(policyMsg)
            return
          }
          if (!this.isValidEmail(account)) {
            this.showAuthFeedback('请填写正确的邮箱')
            return
          }
          if (!this.authForm.name || !this.authForm.gender || !this.authForm.unit || !this.authForm.orgCreditCode) {
            this.showAuthFeedback('请填写姓名、性别、工作/学习单位和单位税号')
            return
          }
          if (!this.registerEmailCodeReady || this.registerEmailCodeEmail !== account.toLowerCase()) {
            this.showAuthFeedback('请先获取邮箱验证码后再提交注册')
            return
          }
          if (!/^\d{6}$/.test((this.authForm.emailOtp || '').trim())) {
            this.showAuthFeedback('邮箱验证码需为6位数字')
            return
          }
          await register({
            email: account.toLowerCase(),
            verificationCode: this.authForm.emailOtp.trim(),
            password: this.authForm.password,
            confirmPassword: this.authForm.confirmPassword,
            name: this.authForm.name.trim(),
            gender: this.authForm.gender,
            unit: this.authForm.unit.trim(),
            orgCreditCode: this.authForm.orgCreditCode.trim(),
            role: this.authForm.role,
            title: this.authForm.title,
            slideTicket: this.authForm.slideTicket
          })
          this.authMode = 'login'
          this.selfServiceStatus = '注册成功，请登录'
          this.showAuthFeedback(this.selfServiceStatus)
          this.resetAuthVerification()
          return
        }

        const result = await login({
          email: account.toLowerCase(),
          password: this.authForm.password,
          slideTicket: this.authForm.slideTicket
        })
        const payload = result.data || result
        saveToken(payload.token)
        this.isLoggedIn = true
        this.selfServiceStatus = '登录成功'
        this.showAuthFeedback(this.selfServiceStatus)
        this.authDialogOpen = false
        this.applyMembershipSession(payload)
        await this.loadSelfService()
        // loadSelfService 成功时已跳转；此处兜底（如资料接口失败仍须改密）
        this.ensureForcePasswordChanged()
      } catch (error) {
        this.authForm.slideTicket = ''
        this.showAuthFeedback(error.message || '操作失败，请稍后重试')
        this.$nextTick(() => {
          if (this.$refs.authModals && typeof this.$refs.authModals.refreshSlideCaptcha === 'function') {
            this.$refs.authModals.refreshSlideCaptcha()
          }
        })
      } finally {
        this.authSubmitting = false
      }
    },
    async chooseMembershipPath(path) {
      if (this.membershipChoiceSubmitting) {
        return
      }
      const userType = path === 'member' ? 'member' : 'non_member'
      this.membershipChoiceSubmitting = true
      try {
        const result = await updateUserType(userType)
        const payload = result.data || result
        this.applyMembershipSession(payload)
        await this.refreshNotifications()
        if (path === 'member') {
          this.goServicesTab('member')
          this.selfServiceStatus = '已选择正式会员路径，可继续提交入会申请'
          return
        }
        this.goServicesTab('branches')
        this.selfServiceStatus = '已选择非会员路径，可继续绑定分会'
      } catch (error) {
        this.selfServiceStatus = error.message || '会员路径保存失败'
      } finally {
        this.membershipChoiceSubmitting = false
      }
    },
    async chooseOrganizationMembershipPath(form) {
      if (this.membershipChoiceSubmitting) {
        return false
      }
      this.membershipChoiceSubmitting = true
      try {
        const result = await requestOrganizationMembership(form)
        const payload = result.data || result
        this.applyMembershipSession(payload)
        await this.refreshNotifications()
        this.goServicesTab('member')
        this.selfServiceStatus = '单位会员关联申请已提交，待秘书处确认后生效'
        return true
      } catch (error) {
        this.selfServiceStatus = error.message || '单位会员关联申请提交失败'
        return false
      } finally {
        this.membershipChoiceSubmitting = false
      }
    },
    async upgradeToMemberPath() {
      await this.chooseMembershipPath('member')
    },
    applyMembershipSession(payload) {
      if (!payload) {
        return
      }
      const previousType = this.userInfo && this.userInfo.userType
      if (payload.user) {
        this.userInfo = payload.user
      }
      if (payload.profile) {
        this.memberProfile = payload.profile
      }
      if (payload.eligibility) {
        this.membershipEligibility = payload.eligibility
      }
      if (payload.forceChangePassword != null && this.userInfo) {
        this.userInfo.forceChangePassword = payload.forceChangePassword
      }
      if (payload.pwdDaysLeft != null || payload.pwdWarn != null || payload.passwordExpired != null) {
        this.pwdDaysLeft = payload.pwdDaysLeft
        this.pwdWarn = !!payload.pwdWarn
      }
      this.membershipPath = payload.user && payload.user.userType === 'member' ? 'member' : 'non_member'
      // 退会等场景身份降级后，关掉退会向导，避免缓存态短暂露出入会/退会流程
      if (this.userInfo && this.userInfo.userType === 'non_member') {
        this.withdrawWizardOpen = false
      }
      if (previousType && this.userInfo && previousType !== this.userInfo.userType) {
        this.persistSelfServiceCache()
      }
    },
    ensureForcePasswordChanged(actionLabel) {
      if (!this.needsForcePasswordChange) {
        return true
      }
      this.selfServiceStatus = actionLabel || '为保障账号安全，请先修改初始密码后再使用其他功能'
      if (this.$route.path !== '/personal-center' || this.$route.query.section !== 'security') {
        // vue-router@3 对重复导航会 reject NavigationDuplicated；登录后 loadSelfService 可能已发起同路由跳转
        this.$router.push({ path: '/personal-center', query: { section: 'security' } }).catch(() => {})
      }
      return false
    },
    onJoinMemberClick() {
      if (!this.isLoggedIn) {
        this.openAuth('register')
        return
      }
      this.goServicesTab('member')
      if (this.needsMembershipChoice) {
        this.$nextTick(() => {
          const el = document.getElementById('membership-path-choice')
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        })
      }
    },
    ensureMembershipChoiceCompleted(actionLabel) {
      if (!this.isLoggedIn) {
        this.openAuth('login')
        this.selfServiceStatus = '请先登录系统'
        return false
      }
      if (!this.ensureForcePasswordChanged()) {
        return false
      }
      if (this.needsMembershipChoice) {
        this.selfServiceStatus = actionLabel || '请先选择参与方式'
        this.goServicesTab('member')
        this.$nextTick(() => {
          const el = document.getElementById('membership-path-choice')
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        })
        return false
      }
      return true
    },
    isValidEmail(account) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(account)
    },
    async logout() {
      try {
        await logoutApi()
      } catch (error) {
        // ignore logout API failures and still clear local session
      }
      clearToken()
      clearSelfServiceSnapshot()
      this.isLoggedIn = false
      this.selfServiceLoading = false
      this.membershipSessionSynced = true
      this.notificationsSynced = true
      this.myNotifications = []
      this.unreadNotificationCount = 0
      this.selfServiceStatus = '已退出登录'
      this.boundBranchCodes = []
      this.myBindings = []
      this.myApplications = []
      this.myPayments = []
      this.myRegistrations = []
      this.publicConferences = []
      this.myContents = []
      this.memberProfile = null
      this.userInfo = null
      this.withdrawWizardOpen = false
      this.accountDropdownOpen = false
      this.applicationForm = {
        applicationType: 'JOIN',
        memberCategory: '',
        applicantName: '',
        foreignFlag: false
      }
      this.profileForm = {
        userName: '',
        gender: '',
        unit: '',
        orgCreditCode: '',
        roleLabel: '教师',
        title: '',
        email: '',
        memberCategory: 'NON_STUDENT'
      }
      this.paymentFeeChoice = 'ANNUAL'
    },
    async fetchUserInfo() {
      try {
        const result = await getUserProfile()
        const payload = result.data || result
        this.userInfo = payload.user || payload || null
        this.memberProfile = payload.profile || this.memberProfile
      } catch (error) {
        this.userInfo = null
      }
    },
    async loadPublicConferences() {
      const result = await listOpenConferences()
      const list = result.data || []
      this.publicConferences = list.map(detail => {
        const conference = detail.conference || detail
        const fees = detail.fees || []
        return {
          ...conference,
          feeRules: {
            studentMember: this.feeAmountByType(fees, 'STUDENT_MEMBER'),
            nonStudentMember: this.feeAmountByType(fees, 'NON_STUDENT_MEMBER'),
            studentNonMember: this.feeAmountByType(fees, 'STUDENT_NON_MEMBER'),
            nonStudentNonMember: this.feeAmountByType(fees, 'NON_STUDENT_NON_MEMBER')
          }
        }
      })
      this.selectConferenceFromServiceBranch()
    },
    selectConferenceFromServiceBranch() {
      if (!this.activeServiceBranchId) {
        return
      }
      if (this.selectedConference && String(this.selectedConference.associationId) === String(this.activeServiceBranchId)) {
        return
      }
      const conference = this.filteredPublicConferences[0]
      if (conference) {
        this.selectConference(conference)
      }
    },
    mapConferenceCard(detail) {
      const conference = detail.conference || detail
      const fees = detail.fees || []
      return {
        conferenceId: conference.conferenceId,
        associationId: conference.associationId,
        title: conference.conferenceTitle || '未命名会议',
        branch: this.associationNameById(conference.associationId),
        time: this.formatDateRange(conference.startDate, conference.endDate),
        city: conference.city || '地点待定',
        status: this.conferenceStatusLabel(conference.status),
        feeRules: {
          studentMember: this.feeAmountByType(fees, 'STUDENT_MEMBER'),
          nonStudentMember: this.feeAmountByType(fees, 'NON_STUDENT_MEMBER'),
          studentNonMember: this.feeAmountByType(fees, 'STUDENT_NON_MEMBER'),
          nonStudentNonMember: this.feeAmountByType(fees, 'NON_STUDENT_NON_MEMBER')
        },
        materialRules: this.buildMaterialRules(conference),
        submissionRules: this.buildSubmissionRules(conference)
      }
    },
    mapContentItem(item) {
      return {
        ...item,
        type: this.contentTypeLabel(item.contentType),
        date: this.formatDate(item.publishTime || item.createTime)
      }
    },
    mapCmsEntry(item) {
      const historyTime = getHistoryTime(item)
      return {
        ...item,
        historyTime,
        importantEvent: getGalleryImportantEvent(item) || String(item.importantEvent || '').trim(),
        date: this.formatDate(historyTime || item.publishTime || item.createTime)
      }
    },
    applySiteConfig(entries) {
      const entry = entries.find(item => item.moduleCode === 'settings' && item.columnCode === 'site_config')
      if (!entry || !entry.extraJson) {
        return
      }
      try {
        const config = typeof entry.extraJson === 'string' ? JSON.parse(entry.extraJson) : (entry.extraJson || {})
        this.siteConfig = {
          copyright: config.copyright || this.siteConfig.copyright,
          contactPhone: config.contactPhone || '',
          contactEmail: config.contactEmail || '',
          address: config.address || '',
          postcode: config.postcode || config.zipCode || '',
          fax: config.fax || config.contactFax || '',
          icp: config.icp || config.icpNumber || '',
          policeRecord: config.policeRecord || config.securityNumber || '',
          wechatQrcode: config.wechatQrcode || config.qrCodeWechat || '',
          weiboQrcode: config.weiboQrcode || config.memberQrcode || config.qrCodeMember || '',
          memberQrcode: config.memberQrcode || config.weiboQrcode || config.qrCodeMember || '',
          friendLinks: (config.friendLinks || []).filter(item => item.name && item.url),
          themePreset: config.themePreset || 'academic',
          themeColor: this.normalizeThemeColor(config.themeColor) || '#002B49',
          galleryHistorySort: config.galleryHistorySort === 'desc' ? 'desc' : 'asc',
          contactUsHtml: typeof config.contactUsHtml === 'string' ? config.contactUsHtml : ''
        }
        this.applyThemePreset(this.siteConfig.themePreset, this.siteConfig.themeColor)
        // Hero CTA 已下线：不再用站点配置 quickLinks 驱动首页按钮
      } catch (error) {
        // Site config is optional; malformed config should not break public browsing.
      }
    },
    normalizeThemeColor(value) {
      const color = String(value || '').trim().toUpperCase()
      return /^#[0-9A-F]{6}$/.test(color) ? color : ''
    },
    mixThemeColor(color, target, amount) {
      const source = color.slice(1).match(/.{2}/g).map(value => parseInt(value, 16))
      const result = source.map(channel => Math.round(channel + (target - channel) * amount))
      return `#${result.map(channel => channel.toString(16).padStart(2, '0')).join('')}`
    },
    applyThemePreset(preset, configuredColor) {
      const presets = {
        academic: {
          '--front-radius': '8px',
          '--front-card-gap': '24px',
          '--front-accent': '#b08a2e'
        },
        compact: {
          '--front-radius': '4px',
          '--front-card-gap': '16px',
          '--front-accent': '#2f7d6d'
        },
        classic: {
          '--front-radius': '6px',
          '--front-card-gap': '28px',
          '--front-accent': '#8f5f36'
        }
      }
      const tokens = presets[preset] || presets.academic
      const primary = this.normalizeThemeColor(configuredColor) || '#002B49'
      tokens['--primary'] = primary
      tokens['--primary-deep'] = this.mixThemeColor(primary, 0, 0.25)
      tokens['--primary-soft'] = this.mixThemeColor(primary, 255, 0.16)
      tokens['--primary-rgb'] = primary.slice(1).match(/.{2}/g).map(value => parseInt(value, 16)).join(', ')
      Object.keys(tokens).forEach(key => {
        document.documentElement.style.setProperty(key, tokens[key])
      })
    },
    toggleLanguage() {
      this.language = this.language === 'zh' ? 'en' : 'zh'
      localStorage.setItem('paleo-front-language', this.language)
      this.publicPageError = this.language === 'en'
        ? 'English mode is enabled. CMS content remains Chinese until English entries are maintained.'
        : ''
    },
    toggleNotificationMenu() {
      this.notificationOpen = !this.notificationOpen
      if (this.notificationOpen && this.isLoggedIn) {
        this.refreshNotifications()
      }
    },
    markVisibleNotificationsRead() {
      if (!this.isLoggedIn) {
        return
      }
      const publicIds = this.systemNotifications
        .filter(item => !item.personalNotificationId)
        .map(item => item.id)
      if (publicIds.length) {
        const merged = Array.from(new Set([...this.readNotificationIds, ...publicIds]))
        if (merged.length !== this.readNotificationIds.length) {
          this.readNotificationIds = merged
          localStorage.setItem('paleo-front-read-notices', JSON.stringify(this.readNotificationIds))
        }
      }
      if (this.unreadNotificationCount) {
        // 空数组：后端将该用户全部未读个人通知标为已读
        this.markPersonalNotificationsRead([])
      }
    },
    markPersonalNotificationsRead(notificationIds) {
      if (!this.isLoggedIn || !Array.isArray(notificationIds)) {
        return Promise.resolve()
      }
      return markMyNotificationsRead(notificationIds)
        .then(() => this.refreshNotifications())
        .catch(() => {})
    },
    openSystemNotification(notice) {
      this.notificationOpen = false
      if (!this.isLoggedIn) {
        this.openAuth('login')
        return
      }
      if (notice && notice.personalNotificationId) {
        this.markPersonalNotificationsRead([notice.personalNotificationId])
        return
      }
      if (notice && notice.id && !this.readNotificationIds.includes(notice.id)) {
        this.readNotificationIds = [...this.readNotificationIds, notice.id]
        localStorage.setItem('paleo-front-read-notices', JSON.stringify(this.readNotificationIds))
      }
      if (notice && notice.raw && (notice.raw.entryId || notice.raw.contentId)) {
        this.openContentDetail(notice.raw)
      }
    },
    isNotificationRead(notice) {
      if (!notice) {
        return true
      }
      if (typeof notice.isRead === 'boolean') {
        return notice.isRead
      }
      if (notice.personalNotificationId) {
        return notice.readFlag === '1' || Boolean(notice.isRead)
      }
      return this.readNotificationIds.includes(notice.id)
    },
    handleQuickLink(link) {
      const target = link.path || link.url
      if (!target) {
        return
      }
      if (/^https?:\/\//.test(target)) {
        window.open(target, '_blank')
        return
      }
      this.goPath(target)
    },
    async submitSearch() {
      const rawKeyword = (this.searchKeyword || '').trim()
      const keyword = rawKeyword.toLowerCase()
      if (!keyword) {
        this.searchResults = []
        this.searchSubmittedKeyword = ''
        return
      }

      this.searchSubmittedKeyword = rawKeyword
      this.searchLoading = true
      try {
        const pageSize = 200
        const [cmsResult, contentResult] = await Promise.all([
          listPublicCmsEntries({ pageNum: 1, pageSize, keyword: rawKeyword }),
          listPublicContents({ pageNum: 1, pageSize, title: rawKeyword })
        ])
        const cmsRows = unwrapPublicPage(cmsResult).rows
        const contentRows = unwrapPublicPage(contentResult).rows
        const matches = (item) => `${item.title || ''} ${item.summary || ''} ${item.bodyContent || ''}`
          .toLowerCase()
          .includes(keyword)
        const toResult = (item, source) => ({
          source,
          id: item.entryId || item.contentId || item.title,
          title: item.title,
          summary: item.summary,
          type: source === 'cms' ? this.contentModuleLabel(item) : this.contentTypeLabel(item.contentType),
          raw: source === 'cms' ? this.mapCmsEntry(item) : this.mapContentItem(item)
        })
        this.searchResults = [
          ...cmsRows.filter(matches).map(item => toResult(item, 'cms')),
          ...contentRows.filter(matches).map(item => toResult(item, 'content'))
        ].slice(0, 12)
        this.publicPageError = ''
      } catch (error) {
        this.searchResults = []
        this.publicPageError = error.message || '搜索失败，请稍后重试'
      } finally {
        this.searchLoading = false
      }
    },
    clearSearch() {
      this.searchResults = []
      this.searchKeyword = ''
      this.searchSubmittedKeyword = ''
    },
    openSearchResult(item) {
      if (item.raw && item.raw.entryId) {
        this.goPath(`/content/${item.raw.entryId}`)
        return
      }
      if (item.raw && item.raw.contentId) {
        this.goPath(`/content/${item.raw.contentId}`)
      }
    },
    mapCmsEntryToContent(item) {
      const publishTypeMap = {
        meeting_notice: 'MEETING_NOTICE',
        important_news: 'IMPORTANT_NEWS',
        party_public: 'PARTY_PUBLIC',
        society_dynamics: 'SOCIETY_DYNAMICS',
        work_progress: 'WORK_PROGRESS'
      }
      const legacyTypeMap = {
        announcements: 'MEETING_NOTICE',
        news: 'IMPORTANT_NEWS',
        party: 'PARTY_PUBLIC'
      }
      let contentType = 'ARTICLE'
      if (item.moduleCode === 'publish') {
        contentType = publishTypeMap[item.columnCode] || 'ARTICLE'
      } else if (legacyTypeMap[item.moduleCode]) {
        contentType = legacyTypeMap[item.moduleCode]
      }
      const routeMap = {
        party: '/party',
        international: '/services?tab=international',
        science: '/services?tab=science',
        'tech-rewards': '/services?tab=rewards',
        awards: '/services?tab=rewards',
        history: '/history',
        timeline: '/history',
        gallery: '/gallery',
        regulations: '/regulations',
        news: '/news',
        announcements: '/society-announcements',
        publish: '/news-publish',
        personnel: '/intro',
        home_portal: '/'
      }
      const original = getOriginalFile(item)
      const attachments = getAttachments(item)
      const primaryAttachment = attachments[0]
      return {
        ...item,
        contentType,
        type: this.contentTypeLabel(contentType),
        title: item.title,
        summary: item.summary,
        bodyContent: item.bodyContent,
        coverImageUrl: item.coverUrl,
        contentUrl: (original && original.url) || (primaryAttachment && primaryAttachment.url) || item.fileUrl || item.linkUrl,
        originalFileUrl: (original && original.url) || (primaryAttachment && primaryAttachment.url) || item.fileUrl,
        originalFileName: (original && original.name)
          || (primaryAttachment && primaryAttachment.name)
          || (item.fileUrl && this.fileNameFromUrl(item.fileUrl)),
        originalFile: original,
        attachments,
        author: item.author,
        viewCount: item.viewCount || 0,
        date: item.date,
        fullDate: item.publishTime || item.createTime || item.date,
        moduleCode: item.moduleCode,
        moduleLabel: this.contentModuleLabel(item),
        modulePath: routeMap[item.moduleCode] || '/news-publish'
      }
    },
    /**
     * 合并 CMS downloads 与公开文件当前页。
     * CMS 无分类接口：在前端按 fileUrl / 分类过滤后再展示与计数。
     */
    mergePublicDownloads(cmsEntries, publicFiles, category = 'ALL') {
      const cmsDownloads = (cmsEntries || [])
        .map(item => (item && item.title !== undefined && item.moduleCode ? item : this.mapCmsEntry(item)))
        .filter(item => item && item.fileUrl)
        .map(item => ({
          fileId: null,
          fileName: item.title,
          fileCategory: this.inferPublicFileCategory(item),
          // /storage/files/{id} 无文件名；优先 CMS 扩展名与标题，供 PDF 预览判断
          originalFileName: (item.fileUrl && this.fileNameFromUrl(item.fileUrl))
            || item.title
            || '',
          fileExtension: item.fileExtension || this.publicFileExtension({
            fileName: item.title,
            originalFileName: item.fileUrl && this.fileNameFromUrl(item.fileUrl)
          }),
          fileUrl: item.fileUrl,
          downloadCount: 0
        }))
        .filter(item => this.matchesPublicFileCategory(item.fileCategory, category))
      const files = (publicFiles || []).filter(item => this.matchesPublicFileCategory(item.fileCategory, category))
      return [...cmsDownloads, ...files]
    },
    publicFileListParams(category, pageNum = 1, pageSize = 5) {
      const params = { pageNum, pageSize }
      if (category && category !== 'ALL') {
        params.fileCategory = category
      }
      return params
    },
    async loadPublicDownloadsPage() {
      const pageNum = this.publicFilesPageNum || 1
      const pageSize = this.publicFilesPageSize || 5
      const category = this.activePublicFileCategory
      const seq = ++this.publicFilesLoadSeq
      try {
        // CMS 与公开文件是两个分页源，必须先完整收集 CMS，再计算文件源的偏移，
        // 否则第 2 页直接请求文件第 2 页会把 CMS 下载从合并结果中漏掉。
        const cmsRows = []
        let cmsPageNum = 1
        let cmsTotal = 0
        do {
          const cmsRes = await listPublicCmsEntries({
            moduleCode: 'downloads',
            pageNum: cmsPageNum,
            pageSize: 200
          })
          const cmsPage = unwrapPublicPage(cmsRes)
          cmsRows.push(...cmsPage.rows)
          cmsTotal = Number(cmsPage.total || cmsRows.length)
          if (cmsPage.rows.length === 0 || cmsRows.length >= cmsTotal) break
          cmsPageNum += 1
        } while (cmsPageNum <= Math.ceil(cmsTotal / 200))

        const cmsEligible = this.mergePublicDownloads(cmsRows, [], category)
        const cmsPageCount = Math.ceil(cmsEligible.length / pageSize)
        const filePageNum = Math.max(1, pageNum - cmsPageCount)
        const filesRes = await listPublicFiles(this.publicFileListParams(category, filePageNum, pageSize))
        if (seq !== this.publicFilesLoadSeq) return
        const filesPage = unwrapPublicPage(filesRes, { pageNum: filePageNum, pageSize })
        const filesMerged = this.mergePublicDownloads([], filesPage.rows, category)
        const rows = pageNum <= cmsPageCount
          ? cmsEligible.slice((pageNum - 1) * pageSize, pageNum * pageSize)
          : filesMerged
        this.publicFiles = rows
        this.publicFilesTotal = cmsEligible.length + Number(filesPage.total || 0)
      } catch (error) {
        if (seq !== this.publicFilesLoadSeq) return
        this.selfServiceStatus = error.message
        if (!this.publicFiles.length) {
          this.publicFiles = []
          this.publicFilesTotal = 0
        }
      }
    },
    changePublicFilesPage(page) {
      this.publicFilesPageNum = page
      this.loadPublicDownloadsPage()
    },
    matchesPublicFileCategory(fileCategory, selected) {
      if (!selected || selected === 'ALL') {
        return true
      }
      const normalized = this.normalizePublicFileCategory(fileCategory)
      return normalized === selected
    },
    normalizePublicFileCategory(value) {
      const raw = String(value || '').trim().toUpperCase()
      if (raw === 'MEDIA') return 'VIDEO'
      if (['CONFERENCE', 'PARTY', 'NEWS_SOURCE', 'ARCHIVE'].includes(raw)) return 'DOCUMENT'
      if (['DOCUMENT', 'AUDIO', 'VIDEO', 'IMAGE'].includes(raw)) return raw
      return 'DOCUMENT'
    },
    inferPublicFileCategory(item) {
      const candidates = [
        item && item.fileCategory,
        item && item.category,
        item && item.columnCode,
        item && item.extraJson && item.extraJson.fileCategory
      ]
      for (let i = 0; i < candidates.length; i += 1) {
        const raw = String(candidates[i] || '').trim().toUpperCase()
        if (['DOCUMENT', 'AUDIO', 'VIDEO', 'IMAGE', 'MEDIA'].includes(raw)) {
          return this.normalizePublicFileCategory(raw)
        }
        if (raw.includes('AUDIO') || raw.includes('音频')) return 'AUDIO'
        if (raw.includes('VIDEO') || raw.includes('视频')) return 'VIDEO'
        if (raw.includes('IMAGE') || raw.includes('PHOTO') || raw.includes('照片') || raw.includes('图片')) return 'IMAGE'
      }
      const path = String((item && (item.fileUrl || item.originalFileName || item.title)) || '')
        .split('?')[0]
        .toLowerCase()
      if (/\.(mp3|wav|aac|flac|m4a|ogg)$/.test(path)) return 'AUDIO'
      if (/\.(mp4|webm|mov|avi|mkv|m4v)$/.test(path)) return 'VIDEO'
      if (/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/.test(path)) return 'IMAGE'
      return 'DOCUMENT'
    },
    contentTypeLabel(value) {
      return {
        MEETING_NOTICE: '会议通知',
        IMPORTANT_NEWS: '重要新闻',
        PARTY_PUBLIC: '党务公开',
        SOCIETY_DYNAMICS: '学会动态',
        WORK_PROGRESS: '工作进展',
        NOTICE: '通知公告',
        ARTICLE: '文章',
        PARTY: '党建文化'
      }[value] || value || '通知公告'
    },
    publicFileCategoryLabel(value) {
      return {
        DOCUMENT: '文档',
        CONFERENCE: '文档',
        PARTY: '文档',
        NEWS_SOURCE: '文档',
        ARCHIVE: '文档',
        AUDIO: '音频',
        VIDEO: '视频',
        MEDIA: '视频',
        IMAGE: '照片',
        ALL: '全部'
      }[value] || value || '公开文件'
    },
    async selectPublicFileCategory(category) {
      this.activePublicFileCategory = category || 'ALL'
      this.publicFilesPageNum = 1
      await this.loadPublicDownloadsPage()
    },
    async openContentDetail(item) {
      if (this.openExternalArticle(item)) {
        return
      }
      const id = item.entryId || item.contentId
      if (id && this.page !== 'content-detail') {
        this.goPath(`/content/${id}`)
        return
      }
      try {
        let content = item
        if (item.entryId) {
          const result = await getPublicCmsEntry(item.entryId)
          content = this.mapCmsEntryToContent(this.mapCmsEntry(result.data || item))
        } else if (item.contentId) {
          const result = await getPublicContent(item.contentId)
          content = result.data || item
        }
        this.selectedContent = item.entryId ? content : this.mapContentItem(content)
        this.$nextTick(() => this.scrollTo('news-detail'))
      } catch (error) {
        this.selfServiceStatus = error.message
        this.selectedContent = item
      }
    },
    async loadRoutedContent() {
      if (this.page !== 'content-detail' || !this.contentId) {
        this.contentDetailLoading = false
        this.contentDetailError = ''
        return
      }
      this.contentDetailLoading = true
      this.contentDetailError = ''
      try {
        // 详情路由使用 CMS entryId；CMS 公开接口负责发布状态和会员门禁，禁止按同一数字 ID 回退旧 contents 表。
        const cmsResult = await getPublicCmsEntry(this.contentId)
        const entry = cmsResult.data || {}
        if (!entry.entryId || ['media', 'settings'].includes(entry.moduleCode)) {
          this.contentDetailError = '内容不存在'
          this.selectedContent = null
          return
        }
        const content = this.mapCmsEntryToContent(this.mapCmsEntry(entry))
        // 轮播已调整为站内文章；兼容历史数据中遗留的外链字段，详情页不再向外跳转。
        if (entry.moduleCode === 'banners') {
          content.linkUrl = ''
        }
        this.selectedContent = content
        this.openExternalArticle(this.selectedContent)
      } catch (error) {
        this.contentDetailError = error.message || '内容加载失败'
        this.selectedContent = null
      } finally {
        this.contentDetailLoading = false
      }
    },
    /**
     * 整篇外链：列表/详情优先新窗口打开，不再进本站正文。
     * 只认 linkUrl，避免把附件 contentUrl 当成跳转地址。
     */
    openExternalArticle(item) {
      if (!item) {
        return false
      }
      const url = String(item.linkUrl || '').trim()
      if (!url) {
        return false
      }
      if (/^https?:\/\//i.test(url)) {
        window.open(url, '_blank', 'noopener')
        return true
      }
      if (url.startsWith('/') && !url.startsWith('//')) {
        this.goPath(url)
        return true
      }
      return false
    },
    async openCmsEntry(item) {
      if (this.openExternalArticle(item)) {
        return
      }
      const linkUrl = item.linkUrl || item.contentUrl || ''

      if (linkUrl && /^\/content\//.test(linkUrl)) {
        this.goPath(linkUrl)
        return
      }

      if (linkUrl && /^https?:\/\//.test(linkUrl)) {
        window.open(this.resolveFileUrl(linkUrl), '_blank')
        return
      }

      if (item.entryId && this.page !== 'content-detail') {
        this.goPath(`/content/${item.entryId}`)
        return
      }

      try {
        let entry = item
        if (item.entryId) {
          const result = await getPublicCmsEntry(item.entryId)
          entry = this.mapCmsEntry(result.data || item)
        }
        this.selectedCmsEntry = entry
        this.$nextTick(() => this.scrollTo('topic-detail'))
      } catch (error) {
        this.selfServiceStatus = error.message
        this.selectedCmsEntry = item
      }
    },
    openCmsAttachment(item) {
      const url = item.fileUrl || item.linkUrl || item.mediaUrl
      if (!url) {
        return
      }
      this.downloadSiteFile(url, item.originalFileName || item.fileName || item.title || this.fileNameFromUrl(url))
    },
    /** 解析 CMS 条目多附件（含历史单附件 fileUrl 兼容） */
    entryAttachments(item) {
      return getAttachments(item || {})
    },
    /** 附件下载展示名：多附件带序号，缺名回退「下载附件」 */
    attachmentLabel(file, index, count) {
      const name = (file && (file.name || this.fileNameFromUrl(file.url))) || ''
      if (count > 1) {
        return `附件${index + 1}${name ? `：${name}` : ''}`
      }
      return name || '下载附件'
    },
    /** 列表内附件下载入口，统一走 downloadSiteFile */
    downloadEntryAttachment(item, file) {
      const url = file && file.url
      if (!url) {
        return
      }
      this.downloadSiteFile(
        url,
        (file && (file.originalName || file.name || this.fileNameFromUrl(url)))
          || (item && item.title)
          || '下载附件'
      )
    },
    /** 内容详情/列表用的模块中文名；首页门户按栏目展示，不暴露 home_portal */
    contentModuleLabel(item) {
      if (!item) {
        return '专题栏目'
      }
      if (item.moduleCode === 'personnel') {
        return '学会简介'
      }
      if (item.moduleCode === 'home_portal') {
        const columns = {
          publications: '出版物',
          dynamics: '学会动态',
          progress: '工作进展',
          notices: '通知公告',
          upcoming_meeting: '近期会议'
        }
        return columns[item.columnCode] || '首页门户'
      }
      return this.moduleLabel(item.moduleCode)
    },
    moduleLabel(value) {
      const known = {
        news: '新闻动态',
        announcements: '会员公告',
        publish: '通知要闻',
        banners: '首页轮播',
        party: '党建文化',
        downloads: '下载中心',
        'public-files': '公开文件',
        timeline: '学会沿革',
        history: '学会沿革',
        home_portal: '首页门户'
      }
      const module = this.cmsFeatureModules.find(item => item.moduleCode === value)
      return (module && module.label) || known[value] || value || '专题栏目'
    },
    async downloadContentOriginal(item) {
      try {
        let target = item
        if (item.originalFileId) {
          const result = await getPublicFileDownloadInfo(item.originalFileId)
          target = {
            ...item,
            fileUrl: result.data && result.data.fileUrl
          }
        }
        const url = target.fileUrl || target.originalFileUrl || target.contentUrl
        if (!url) {
          this.selfServiceStatus = '暂无法下载该文件'
          return
        }
        await this.downloadSiteFile(
          url,
          target.originalFileName || target.fileName || this.fileNameFromUrl(url)
        )
      } catch (error) {
        this.selfServiceStatus = error.message
      }
    },
    previewPublicFile(file) {
      // 兼容旧调用：交由资料下载区弹层预览（含第三方 Office / PDF）
      if (!file) {
        this.selfServiceStatus = '暂无法预览该文件'
        return
      }
      this.$root.$emit('paleo-preview-public-file', file)
    },
    publicFileExtension(file) {
      if (!file) return ''
      const fromField = String(file.fileExtension || '').replace(/^\./, '').trim().toLowerCase()
      if (fromField) return fromField
      const name = String(file.originalFileName || file.fileName || file.fileUrl || '')
      const matched = name.toLowerCase().match(/\.([a-z0-9]+)(?:\?|#|$)/)
      return matched ? matched[1] : ''
    },
    isPdfPublicFile(file) {
      return this.publicFileExtension(file) === 'pdf'
    },
    isOfficePublicFile(file) {
      return ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(this.publicFileExtension(file))
    },
    /**
     * 供第三方预览抓取的绝对公网地址。
     * 优先业务库中的 https fileUrl（常为 OSS）；本机相对路径无法被 Office Online 访问。
     */
    absolutePublicFileUrl(file) {
      if (!file) return ''
      const raw = file.fileUrl || file.originalFileUrl || ''
      if (/^https?:\/\//i.test(raw)) {
        return raw
      }
      const access = this.publicFileAccessUrl(file)
      if (!access) return ''
      if (/^https?:\/\//i.test(access)) {
        return access
      }
      try {
        return new URL(access, window.location.origin).href
      } catch (e) {
        return ''
      }
    },
    /** Microsoft Office Online 嵌入预览；非 Office 或无可公网 URL 时返回空 */
    officeOnlinePreviewUrl(file) {
      if (!this.isOfficePublicFile(file)) {
        return ''
      }
      const abs = this.absolutePublicFileUrl(file)
      if (!abs || !/^https:\/\//i.test(abs)) {
        return ''
      }
      // 内网/本机地址 Office Online 无法拉取
      try {
        const host = new URL(abs).hostname
        if (host === 'localhost' || host === '127.0.0.1' || /^192\.168\./.test(host) || /^10\./.test(host)) {
          return ''
        }
      } catch (e) {
        return ''
      }
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(abs)}`
    },
    /**
     * 预览/播放地址：
     * - 有 fileId：走匿名 /paleo/public-files/{id}/content（inline）
     * - CMS 等无 fileId 的 /storage|/profile：走 /paleo/cms/public/file（勿直开 storage，否则 attachment 会触发浏览器下载、弹层空白）
     */
    publicFileAccessUrl(file) {
      if (!file) return ''
      if (file.fileId) {
        return this.resolveFileUrl(`/paleo/public-files/${file.fileId}/content`)
      }
      const url = file.fileUrl
      return url ? this.resolvePublicDisplayUrl(url) : ''
    },
    isBrowserSameOriginUrl(url) {
      if (!url) return false
      if (!/^https?:\/\//i.test(url)) {
        return true
      }
      try {
        return new URL(url, window.location.origin).origin === window.location.origin
      } catch (e) {
        return false
      }
    },
    async downloadPublicFile(file) {
      try {
        this.selfServiceStatus = ''
        let fileName = (file && (file.originalFileName || file.fileName)) || 'download'
        let fullUrl = this.publicFileAccessUrl(file)
        if (!fullUrl && file && file.fileId) {
          const result = await getPublicFileDownloadInfo(file.fileId)
          const target = result.data || {}
          fileName = target.originalFileName || target.fileName || fileName
          fullUrl = this.publicFileAccessUrl({ ...file, ...target })
        }
        if (!fullUrl) {
          this.selfServiceStatus = resolveDownloadErrorMessage({ status: 404, message: '文件不存在' })
          return
        }
        // 外链受 CORS 限制，fetch blob 会失败；回退为新窗口打开
        if (!this.isBrowserSameOriginUrl(fullUrl)) {
          const opened = window.open(fullUrl, '_blank', 'noopener')
          if (!opened) {
            this.selfServiceStatus = '无法打开外链下载，请检查浏览器拦截或稍后重试'
          }
          return
        }
        const token = localStorage.getItem('paleo_user_token') || ''
        const response = await fetch(fullUrl, {
          method: 'GET',
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        })
        if (!response.ok) {
          let serverMsg = ''
          try {
            const ct = (response.headers.get('content-type') || '').toLowerCase()
            if (ct.includes('application/json')) {
              const data = await response.json()
              serverMsg = (data && data.msg) || ''
            }
          } catch (e) { /* ignore */ }
          const err = new Error(serverMsg || '文件下载失败')
          err.status = response.status
          throw err
        }
        const blob = await response.blob()
        const objectUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = objectUrl
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        link.remove()
        setTimeout(() => URL.revokeObjectURL(objectUrl), 30000)
        this.selfServiceStatus = ''
      } catch (error) {
        this.selfServiceStatus = resolveDownloadErrorMessage(error)
      }
    },
    feeAmountByType(fees, type) {
      const fee = fees.find(item => item.feeType === type && item.status !== '1')
      return fee && fee.amount !== undefined && fee.amount !== null ? fee.amount : null
    },
    buildMaterialRules(conference) {
      const rules = []
      if (conference.publicNoticeUrl) {
        rules.push('可下载公开通知')
      }
      if (conference.sealedNoticeUrl) {
        rules.push('缴费确认后下载盖章通知')
      }
      if (conference.abstractTemplateUrl) {
        rules.push('缴费确认后下载摘要模板')
      }
      return rules.length ? rules : ['会议资料由后台维护']
    },
    buildSubmissionRules(conference) {
      return [
        conference.abstractDeadline ? `摘要截止：${this.formatDate(conference.abstractDeadline)}` : '',
        conference.accommodationDeadline ? `住宿截止：${this.formatDate(conference.accommodationDeadline)}` : '',
        conference.fieldTripDeadline ? `野外报名截止：${this.formatDate(conference.fieldTripDeadline)}` : ''
      ].filter(Boolean)
    },
    conferenceStatusLabel(status) {
      return {
        OPEN: '报名中',
        CLOSED: '已截止',
        DRAFT: '待发布'
      }[status] || status || '报名中'
    },
    async loadMembershipBankAccount() {
      try {
        const result = await getMembershipBankAccount()
        const data = result.data || result
        if (!data || !data.accountName) {
          return
        }
        this.membershipBankAccount = data
        this.offlinePaymentInfo = {
          account: `${data.accountName} · ${data.bankName} · ${data.accountNumber}`,
          remark: data.remarkHint || '汇款备注建议填写：姓名 + 学会会费',
          upload: '线下汇款后请上传缴费凭证；凭证初审通过后，请在截止日前上传电子发票。'
        }
      } catch (error) {
        // keep default offline payment hint
      }
    },
    async loadMembershipFeeConfig() {
      try {
        const result = await getMembershipFeeConfig()
        const data = result.data || result || []
        if (Array.isArray(data)) {
          this.membershipFeeConfigs = data
        }
      } catch (error) {
        // keep default fallback (100/200)
        this.membershipFeeConfigs = []
      }
    },
    async loadMembershipTemplates() {
      try {
        const result = await getPublicMembershipTemplates()
        const data = result.data || result || {}
        this.membershipTemplates = {
          join: data.join || null,
          withdraw: data.withdraw || null
        }
      } catch (error) {
        this.membershipTemplates = { join: null, withdraw: null }
      }
    },
    applicationReviewStatusLabel(status) {
      return {
        PENDING: '审核中',
        APPROVED: '已通过',
        REJECTED: '已驳回',
        CANCELLED: '已撤销'
      }[status] || status || '未知'
    },
    applicationTypeLabel(type) {
      return type === 'WITHDRAW' || type === 'QUIT' ? '退会' : '入会'
    },
    /** 统一经鉴权通道下载业务文件（会议资料/凭证等）。可选传入展示用文件名。 */
    async openSecureFile(url, fileName = '') {
      if (!url) {
        return
      }
      await this.downloadSiteFile(url, fileName || this.fileNameFromUrl(url))
    },
    /**
     * 站点文件统一下载：
     * - 已发布 CMS 引用的 /storage/files|/profile → 匿名代理 /paleo/cms/public/file
     * - 会议盖章通知/摘要模板等 → /paleo/files/secure（勿直开 /storage/files）
     * - 公开文件 content / 外链按原策略处理
     */
    async downloadSiteFile(url, fileName = '') {
      const raw = String(url || '').trim()
      if (!raw) {
        this.selfServiceStatus = '文件地址为空'
        return
      }
      const relative = this.normalizeSiteFilePath(raw)
      // 外链（OSS/CDN 等）保持原地址打开，禁止剥成 /paleo/... 再拼到本站 API
      if (/^https?:\/\//i.test(raw) && !relative) {
        const opened = window.open(raw, '_blank', 'noopener')
        if (!opened) {
          this.selfServiceStatus = '无法打开外链，请检查浏览器拦截或稍后重试'
        }
        return
      }
      const path = relative || (raw.startsWith('/') ? raw : `/${raw}`)
      // 历史误存的相对 /paleo/年/月/日/ 路径不是后端接口，不能走 /dev-api
      if (/^\/paleo\/\d{4}\//.test(path)) {
        this.selfServiceStatus = resolveDownloadErrorMessage({ status: 404, message: '文件地址不存在' })
        return
      }
      if (/\/paleo\/public-files\/\d+\/content(?:$|\?)/.test(path)) {
        try {
          await this.fetchBlobDownload(this.resolveFileUrl(path), fileName || this.fileNameFromUrl(path), true)
        } catch (error) {
          this.selfServiceStatus = resolveDownloadErrorMessage(error)
        }
        return
      }
      if (isStorageOrProfilePath(path)) {
        const cleanPath = path.replace(/[?#].*$/, '')
        const name = fileName || this.fileNameFromUrl(cleanPath)
        const proxyUrl = `${process.env.VUE_APP_BASE_API || ''}/paleo/cms/public/file?url=${encodeURIComponent(cleanPath)}`
        try {
          await this.fetchBlobDownload(proxyUrl, name, true)
          return
        } catch (cmsError) {
          try {
            // 会议盖章通知/摘要模板/凭证等：走权限校验通道，禁止直开 /storage/files
            await downloadSecureFile(cleanPath, name)
            return
          } catch (secureError) {
            const msg = (secureError && secureError.message)
              || (cmsError && cmsError.message)
              || '文件下载失败'
            if (secureError && secureError.status === 403) {
              this.selfServiceStatus = '暂无下载权限（需缴费确认后开放）'
            } else if (secureError && secureError.status === 401) {
              this.authMode = 'login'
              this.authDialogOpen = true
              this.selfServiceStatus = /过期/.test(String(msg)) ? msg : '请登录后再下载该文件'
              this.showAuthFeedback(this.selfServiceStatus)
            } else {
              this.selfServiceStatus = resolveDownloadErrorMessage(secureError || cmsError)
            }
            return
          }
        }
      }
      try {
        await this.fetchBlobDownload(this.resolveFileUrl(path), fileName || this.fileNameFromUrl(path), true)
      } catch (error) {
        this.selfServiceStatus = resolveDownloadErrorMessage(error)
      }
    },
    /**
     * 预览用地址：/storage|/profile 经 secure 拉 blob，避免 iframe 直开 401。
     * 调用方在替换前应 revoke 旧 blob URL。
     */
    async resolvePreviewObjectUrl(url) {
      const raw = String(url || '').trim()
      if (!raw) {
        return ''
      }
      const relative = this.normalizeSiteFilePath(raw)
      if (/^https?:\/\//i.test(raw) && !relative) {
        return raw
      }
      const path = (relative || (raw.startsWith('/') ? raw : `/${raw}`)).replace(/[?#].*$/, '')
      if (isStorageOrProfilePath(path)) {
        try {
          const proxyUrl = `${process.env.VUE_APP_BASE_API || ''}/paleo/cms/public/file?url=${encodeURIComponent(path)}`
          const response = await fetch(proxyUrl, { method: 'GET' })
          const contentType = (response.headers.get('content-type') || '').toLowerCase()
          if (response.ok && !contentType.includes('application/json')) {
            return URL.createObjectURL(await response.blob())
          }
        } catch (e) {
          // fall through to secure
        }
        return fetchSecureFileObjectUrl(path, { clearSessionOnAuthFailure: false })
      }
      return this.resolveFileUrl(path)
    },
    /**
     * 仅把「本站后端」绝对地址收成相对路径；OSS 等外链返回空串，避免误拼 /dev-api。
     */
    normalizeSiteFilePath(url) {
      const apiBase = process.env.VUE_APP_BASE_API || ''
      const ownOrigin = typeof window !== 'undefined' ? window.location.origin : ''
      const relative = toSiteRelativeFilePath(url, apiBase, ownOrigin)
      if (relative) {
        return relative
      }
      // 绝对外链已由 toSiteRelativeFilePath 返回 ''；本站绝对地址若未命中后缀规则再兜底
      const raw = String(url || '').trim()
      if (!raw || !/^https?:\/\//i.test(raw)) {
        return relative
      }
      try {
        const parsed = new URL(raw)
        if (!this.isOwnBackendAbsoluteUrl(parsed)) {
          return ''
        }
      } catch (e) {
        return ''
      }
      return ''
    },
    isOwnBackendAbsoluteUrl(parsedOrUrl) {
      try {
        const parsed = typeof parsedOrUrl === 'string' ? new URL(parsedOrUrl) : parsedOrUrl
        const apiBase = process.env.VUE_APP_BASE_API || ''
        if (/^https?:\/\//i.test(apiBase)) {
          return parsed.origin === new URL(apiBase).origin
        }
        return typeof window !== 'undefined' && parsed.origin === window.location.origin
      } catch (e) {
        return false
      }
    },
    async fetchBlobDownload(fullUrl, fileName, withAuth) {
      const headers = {}
      if (withAuth) {
        const token = localStorage.getItem('paleo_user_token') || ''
        if (token) {
          headers.Authorization = `Bearer ${token}`
        }
      }
      const response = await fetch(fullUrl, { method: 'GET', headers })
      const contentType = (response.headers.get('content-type') || '').toLowerCase()
      if (!response.ok || contentType.includes('application/json')) {
        let message = '文件下载失败'
        try {
          const data = await response.json()
          if (data && data.msg) {
            message = data.msg
          }
        } catch (e) {
          if (response.status === 403) {
            message = '无权下载该文件或文件未公开发布'
          } else if (response.status === 401) {
            message = '请登录后下载'
          }
        }
        const mapped = resolveDownloadErrorMessage({ status: response.status, message })
        const err = new Error(mapped)
        err.status = response.status
        throw err
      }
      const blob = await response.blob()
      const objectUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = fileName || 'download'
      document.body.appendChild(link)
      link.click()
      link.remove()
      setTimeout(() => URL.revokeObjectURL(objectUrl), 30000)
    },
    async downloadMembershipTemplate(kind) {
      const template = kind === 'withdraw' ? this.membershipTemplates.withdraw : this.membershipTemplates.join
      if (!template || !template.fileUrl) {
        this.selfServiceStatus = '当前暂无可用模板，请直接上传您的申请书'
        return
      }
      try {
        const fileName = template.fileName || (kind === 'withdraw' ? '退会申请书模板' : '入会申请书模板')
        await downloadStorageFile(template.fileUrl, fileName)
      } catch (error) {
        this.selfServiceStatus = resolveDownloadErrorMessage(error)
      }
    },
    async cancelApplication(applicationId) {
      try {
        await cancelMyApplication(applicationId)
        await this.loadSelfService()
        this.selfServiceStatus = '申请已撤销'
      } catch (error) {
        this.selfServiceStatus = error.message
      }
    },
    async cancelMembershipPayment(paymentId) {
      if (!paymentId) {
        this.selfServiceStatus = '未找到可取消的会费草稿'
        return false
      }
      try {
        await cancelMyPayment(paymentId)
        this.uploadSelection.paymentId = null
        await this.loadSelfService()
        this.selfServiceStatus = '已取消缴费，可稍后再次开始缴纳会费'
        this.$nextTick(() => {
          const el = document.getElementById('membership-application')
            || document.querySelector('.membership-application-card')
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        })
        return true
      } catch (error) {
        this.selfServiceStatus = error.message || '取消缴费失败'
        return false
      }
    },
    selectMembershipApplicationType(applicationType) {
      this.applicationForm.applicationType = applicationType
      if (this.userInfo) {
        this.applicationForm.applicantName = this.userInfo.userName || ''
      }
      if (applicationType === 'JOIN') {
        this.initJoinMemberTypeDefaults()
      }
      this.selfServiceStatus = ''
    },
    initJoinMemberTypeDefaults() {
      if (!this.isLoggedIn) {
        return
      }
      if (this.applicationForm.applicationType !== 'JOIN') {
        return
      }
      this.applicationForm.joinGroup = 'annual'
      this.applicationForm.termStartDate = ''
      this.applicationForm.termEndDate = ''
      if (this.applicationForm.foreignFlag) {
        this.selectJoinMemberType('FOREIGN')
        return
      }
      const isStudent = this.userInfo && String(this.userInfo.isStudent) === '1'
      this.selectJoinMemberType(isStudent ? 'STUDENT' : 'NON_STUDENT')
    },
    selectJoinMemberType(type) {
      const normalized = String(type || '').toUpperCase()
      const council = ['PRESIDENT', 'VICE_PRESIDENT', 'STANDING_DIRECTOR', 'DIRECTOR']
      if (council.indexOf(normalized) !== -1) {
        this.applicationForm.joinGroup = 'council'
        this.applicationForm.foreignFlag = false
        this.applicationForm.memberCategory = normalized
        return
      }
      this.applicationForm.joinGroup = 'annual'
      if (normalized === 'FOREIGN') {
        this.applicationForm.foreignFlag = true
        this.applicationForm.memberCategory = 'FOREIGN'
        this.applicationForm.termStartDate = ''
        this.applicationForm.termEndDate = ''
        return
      }
      this.applicationForm.foreignFlag = false
      this.applicationForm.termStartDate = ''
      this.applicationForm.termEndDate = ''
      if (normalized === 'STUDENT') {
        this.applicationForm.memberCategory = 'STUDENT'
        return
      }
      this.applicationForm.memberCategory = 'NON_STUDENT'
    },
    selectJoinGroup(group) {
      const normalized = group === 'council' ? 'council' : 'annual'
      this.applicationForm.joinGroup = normalized
      if (normalized === 'council') {
        this.applicationForm.foreignFlag = false
        if (['PRESIDENT', 'VICE_PRESIDENT', 'STANDING_DIRECTOR', 'DIRECTOR']
          .indexOf(this.applicationForm.memberCategory) === -1) {
          this.applicationForm.memberCategory = 'DIRECTOR'
        }
        return
      }
      this.applicationForm.termStartDate = ''
      this.applicationForm.termEndDate = ''
      if (this.applicationForm.foreignFlag || this.applicationForm.memberCategory === 'FOREIGN') {
        this.selectJoinMemberType('FOREIGN')
        return
      }
      const isStudent = this.userInfo && String(this.userInfo.isStudent) === '1'
      this.selectJoinMemberType(isStudent ? 'STUDENT' : 'NON_STUDENT')
    },
    syncApplicationMemberCategory() {
      if (!this.isLoggedIn) {
        return
      }
      if (this.applicationForm.applicationType !== 'JOIN') {
        return
      }
      if (this.applicationForm.joinGroup === 'council') {
        const cat = String(this.applicationForm.memberCategory || '').toUpperCase()
        if (['PRESIDENT', 'VICE_PRESIDENT', 'STANDING_DIRECTOR', 'DIRECTOR'].indexOf(cat) !== -1) {
          this.applicationForm.memberCategory = cat
          this.applicationForm.foreignFlag = false
          return
        }
        this.applicationForm.memberCategory = 'DIRECTOR'
        this.applicationForm.foreignFlag = false
        return
      }
      if (this.applicationForm.foreignFlag) {
        this.applicationForm.memberCategory = 'FOREIGN'
        return
      }
      if (this.applicationForm.memberCategory === 'STUDENT'
          || this.applicationForm.memberCategory === 'NON_STUDENT') {
        return
      }
      this.applicationForm.memberCategory = this.membershipFeeCategory
    },
    validateJoinApplicationForm() {
      // 任期仅入会申请理事类需要；退会等其他类型不校验
      if (this.applicationForm.applicationType !== 'JOIN') {
        return true
      }
      this.syncApplicationMemberCategory()
      if (this.applicationForm.joinGroup === 'council'
          || ['PRESIDENT', 'VICE_PRESIDENT', 'STANDING_DIRECTOR', 'DIRECTOR']
            .indexOf(String(this.applicationForm.memberCategory || '').toUpperCase()) !== -1) {
        const start = this.applicationForm.termStartDate
        const end = this.applicationForm.termEndDate
        if (!start || !end) {
          this.selfServiceStatus = '申请理事类会员须填写任期起止日期'
          return false
        }
        if (String(end) <= String(start)) {
          this.selfServiceStatus = '任期结束日期须晚于开始日期'
          return false
        }
      }
      return true
    },
    toggleForeignMemberFlag(checked) {
      if (checked) {
        this.selectJoinMemberType('FOREIGN')
      } else {
        const isStudent = this.userInfo && String(this.userInfo.isStudent) === '1'
        this.selectJoinMemberType(isStudent ? 'STUDENT' : 'NON_STUDENT')
      }
    },
    focusMembershipPayment() {
      this.goServicesTab('member')
      this.selfServiceStatus = ''
      this.$nextTick(() => {
        const el = document.getElementById('membership-payment')
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },
    startWithdrawApplication() {
      if (!this.canSubmitWithdrawApplication) {
        this.selfServiceStatus = '仅有效会员可以发起退会申请'
        return
      }
      this.withdrawWizardOpen = true
      this.selectMembershipApplicationType('WITHDRAW')
      this.goServicesTab('member')
      this.selfServiceStatus = '请阅读退会须知、下载申请书模板后提交申请'
    },
    paymentStatusLabel(status) {
      return {
        VOUCHER_REVIEW: '凭证审核中',
        VOUCHER_APPROVED: '凭证通过',
        VOUCHER_REJECTED: '凭证驳回',
        INVOICE_PENDING: '待上传发票',
        INVOICE_OVERDUE: '发票逾期未传',
        INVOICE_REVIEW: '发票审核中',
        INVOICE_REJECTED: '发票驳回',
        CONFIRMED: '已确认',
        VOIDED: '已作废',
        UNPAID: '待缴费',
        FROZEN: '会员到期冻结'
      }[status] || status || '待提交'
    },
    membershipCategoryLabel(category) {
      const map = {
        STUDENT: '学生会员',
        NON_STUDENT: '普通会员',
        FOREIGN: '外籍会员',
        PERMANENT: '终身会员',
        ANNUAL: '年费会员',
        PRESIDENT: '理事长',
        VICE_PRESIDENT: '副理事长',
        STANDING_DIRECTOR: '常务理事',
        DIRECTOR: '理事'
      }
      const key = String(category || '').toUpperCase()
      return map[key] || (key ? key : '会员费')
    },
    /** 站内通知 category 为内部键，展示给用户时映射为中文；禁止露出英文标识 */
    notificationCategoryLabel(category) {
      const raw = String(category || '').trim()
      if (!raw) {
        return '会员服务'
      }
      const map = {
        conference_published: '新会议发布',
        conference_payment_deadline: '会议缴费提醒',
        payment_review: '缴费审核',
        invoice_deadline: '发票截止提醒',
        membership_expiry: '会员到期',
        membership_path: '参与方式',
        membership_choice: '参与方式',
        application_review: '申请审核',
        membership_application: '申请审核',
        branch_binding: '分会绑定',
        attendee_info: '参会信息',
        profile: '个人资料',
        member_cert: '电子会员证',
        lifetime_upgrade: '终身会员',
        membership_admin: '会员管理'
      }
      const key = raw.toLowerCase()
      if (map[key]) {
        return map[key]
      }
      // 任意英文内部键（含 payment_review / MEMBERSHIP_CHOICE 等）一律不直出
      if (/^[A-Za-z][A-Za-z0-9_]*$/.test(raw)) {
        return '系统通知'
      }
      return raw
    },
    /** 通知列表类型文案：个人通知强制中文化，公开公告保留中文栏目名 */
    displayNotificationType(notice) {
      if (!notice) {
        return '系统通知'
      }
      if (notice.personalNotificationId) {
        const rawCategory = notice.raw && notice.raw.category
        return this.notificationCategoryLabel(rawCategory || notice.type)
      }
      const label = String(notice.type || '').trim()
      if (!label) {
        return '通知'
      }
      if (/^[A-Za-z][A-Za-z0-9_]*$/.test(label)) {
        return this.notificationCategoryLabel(label)
      }
      return label
    },
    registrationStatusLabel(registration) {
      if (!registration) {
        return '待提交'
      }
      if (registration.frozenDueToExpiry === '1') {
        return '会员到期冻结'
      }
      if (registration.paymentStatus === 'INVOICE_OVERDUE') {
        return '发票逾期未传'
      }
      if (registration.status === 'VOIDED') {
        return '已作废'
      }
      return this.paymentStatusLabel(registration.paymentStatus)
    },
    formatDate(value) {
      return value ? String(value).slice(0, 10) : ''
    },
    formatDateTime(value) {
      if (!value) return '时间待定'
      const text = String(value).replace('T', ' ')
      // 保留到分钟：2026-08-12 15:30
      if (text.length >= 16) {
        return text.slice(0, 16)
      }
      return this.formatDate(text) || '时间待定'
    },
    formatDateRange(startDate, endDate) {
      const start = this.formatDate(startDate)
      const end = this.formatDate(endDate)
      if (start && end && start !== end) {
        return `${start} 至 ${end}`
      }
      return start || end || '时间待定'
    },
    formatFee(value) {
      return value === undefined || value === null || value === '' ? '待定' : `¥${value}`
    },
    conferenceTitleById(conferenceId) {
      const conference = this.publicConferences.find(item => String(item.conferenceId) === String(conferenceId))
        || this.conferences.find(item => String(item.conferenceId) === String(conferenceId))
      return (conference && (conference.title || conference.conferenceTitle)) || `会议 #${conferenceId || ''}`
    },
    conferenceRouteId(conference, index) {
      return conference.conferenceId || `local-${index}`
    },
    openConferenceDetail(conference, index) {
      this.goPath(`/conference/${this.conferenceRouteId(conference, index)}`)
    },
    async startConferenceRegistration(conference) {
      this.goServicesTab('conference', { keepView: true })
      await this.startConferencePayment(conference)
    },
    normalizeAssociation(association) {
      const branchCode = association.branchCode || BRANCH_CODE_BY_ASSOCIATION_ID[association.associationId] || ''
      return {
        ...association,
        branchCode
      }
    },
    syncBindingState() {
      this.myBindings = this.branchAssociations
        .filter(item => this.boundBranchCodes.includes(item.branchCode))
        .map(item => ({
          associationId: item.associationId,
          bindingStatus: 'BOUND',
          branchCode: item.branchCode
        }))
    },
    isMainSociety(item) {
      return item && (item.associationType === 'MAIN' || item.associationId === 1 || item.branchCode === 'zgswxh')
    },
    /** 学术会议列表可见：总学会始终可见；分会须已绑定 */
    isConferenceVisibleInList(conference) {
      if (!conference) {
        return false
      }
      if (Number(conference.associationId) === 1) {
        return true
      }
      const association = (this.bindableAssociations || []).find(
        item => String(item.associationId) === String(conference.associationId)
      )
      if (association) {
        return this.isAssociationBound(association)
      }
      const branchCode = BRANCH_CODE_BY_ASSOCIATION_ID[conference.associationId]
      return !!(branchCode && this.boundBranchCodes.includes(branchCode))
    },
    isAssociationBound(item) {
      if (this.isMainSociety(item)) {
        return this.isLoggedIn
      }
      return item && item.branchCode && this.boundBranchCodes.includes(item.branchCode)
    },
    async toggleAssociation(item) {
      if (this.isMainSociety(item)) {
        this.selfServiceStatus = '总学会默认已绑定，无需操作'
        return
      }
      if (!this.ensureMembershipChoiceCompleted('请先选择参与方式后再绑定分会')) {
        return
      }
      if (!item.branchCode) {
        this.selfServiceStatus = '分会编码缺失，无法操作'
        return
      }
      try {
        if (this.isAssociationBound(item)) {
          const result = await unbindBranch(item.branchCode)
          this.boundBranchCodes = result.data || result.branchCodes || []
          this.selfServiceStatus = `已解绑分会：${item.associationName}`
        } else {
          const result = await bindBranch(item.branchCode)
          this.boundBranchCodes = result.data || result.branchCodes || []
          this.selfServiceStatus = `成功绑定分会：${item.associationName}`
        }
        this.syncBindingState()
        await this.loadPublicConferences()
      } catch (error) {
        this.selfServiceStatus = error.message
      }
    },
    async selectConference(conference) {
      this.selectedConference = conference
      this.selectedConferenceFeeQuote = null
      this.selectedMaterials = {
        publicNoticeUrl: conference && conference.publicNoticeUrl ? conference.publicNoticeUrl : null
      }
      try {
        const [materials, quote] = await Promise.all([
          this.isLoggedIn
            ? getConferenceMaterials(conference.conferenceId).catch(() => null)
            : Promise.resolve(null),
          this.isLoggedIn ? getConferenceFeeQuote(conference.conferenceId).catch(() => null) : Promise.resolve(null)
        ])
        if (materials && materials.data) {
          this.selectedMaterials = materials.data
        }
        this.selectedConferenceFeeQuote = quote && (quote.data || quote) || null
      } catch (error) {
        this.selectedConferenceFeeQuote = null
      }
    },
    async refreshNotifications() {
      if (!this.isLoggedIn) {
        return
      }
      try {
        const result = await listMyNotifications({ limit: 50 })
        const data = result.data || result || {}
        this.myNotifications = data.notifications || []
        this.unreadNotificationCount = data.unreadCount || 0
      } catch (error) {
        // Notifications are supplemental and must not roll back a completed business action.
      }
    },
    async submitMembershipApplication(file) {
      if (!this.ensureMembershipChoiceCompleted('请先选择参与方式')) {
        return false
      }
      if (this.userInfo && this.userInfo.userType === 'non_member' && this.applicationForm.applicationType === 'JOIN') {
        this.selfServiceStatus = '请先升级为正式会员路径后再提交入会申请'
        return false
      }
      if (!file) {
        this.selfServiceStatus = '请先选择申请书文件'
        return false
      }
      if (!this.validateSelectedFile(file, 'application')) {
        return false
      }
      let createdApplication = null
      this.syncApplicationMemberCategory()
      if (!this.validateJoinApplicationForm()) {
        return false
      }
      try {
        const payload = { ...this.applicationForm }
        delete payload.joinGroup
        delete payload.foreignFlag
        if (!payload.termStartDate) {
          delete payload.termStartDate
        }
        if (!payload.termEndDate) {
          delete payload.termEndDate
        }
        const result = await addMyApplication(payload)
        createdApplication = result.data || result
        if (!createdApplication || !createdApplication.applicationId) {
          throw new Error('申请创建失败，请重试')
        }
        await uploadMyApplicationFile(createdApplication.applicationId, file)
        await this.loadSelfService()
        this.selfServiceStatus = '申请已提交，等待秘书处审核'
        return true
      } catch (error) {
        if (createdApplication && createdApplication.applicationId) {
          try {
            await cancelMyApplication(createdApplication.applicationId)
          } catch (cancelError) {
            // Keep the original error message. The user can still see and cancel the pending record later.
          }
        }
        this.selfServiceStatus = error.message
        return false
      }
    },
    async submitApplication() {
      return this.submitMembershipApplication(null)
    },
    async submitProfile() {
      try {
        await updateUserProfile({
          userName: this.profileForm.userName,
          gender: this.profileForm.gender,
          unit: this.profileForm.unit,
          role: this.profileForm.roleLabel,
          title: this.profileForm.title,
          orgCreditCode: this.profileForm.orgCreditCode || ''
        })
        await this.loadSelfService()
        this.selfServiceStatus = '个人资料已保存'
      } catch (error) {
        this.selfServiceStatus = error.message
      }
    },
    async submitChangePassword() {
      try {
        if (!this.isLoggedIn) {
          this.selfServiceStatus = '请先登录后再修改密码'
          this.showAuthFeedback(this.selfServiceStatus)
          return
        }
        const oldPassword = this.passwordForm.oldPassword || ''
        const newPassword = this.passwordForm.newPassword || ''
        const confirmPassword = this.passwordForm.confirmPassword || ''
        if (!oldPassword || !newPassword) {
          this.selfServiceStatus = '请填写原密码和新密码'
          this.showAuthFeedback(this.selfServiceStatus)
          return
        }
        if (newPassword !== confirmPassword) {
          this.selfServiceStatus = '两次输入的新密码不一致'
          this.showAuthFeedback(this.selfServiceStatus)
          return
        }
        const policyMsg = validatePasswordPolicy(newPassword, this.userInfo && this.userInfo.email)
        if (policyMsg) {
          this.selfServiceStatus = policyMsg
          this.showAuthFeedback(this.selfServiceStatus)
          return
        }
        await changePassword({ oldPassword, newPassword, confirmPassword })
        clearToken()
        this.isLoggedIn = false
        this.userInfo = null
        this.memberProfile = null
        this.passwordForm = { oldPassword: '', newPassword: '', confirmPassword: '' }
        this.selfServiceStatus = '密码已修改，请重新登录'
        this.authMode = 'login'
        this.authDialogOpen = true
        this.showAuthFeedback(this.selfServiceStatus)
      } catch (error) {
        this.selfServiceStatus = error.message || '修改密码失败，请稍后重试'
        this.showAuthFeedback(this.selfServiceStatus)
      }
    },
    async submitCancelAccount() {
      if (this.needsForcePasswordChange) {
        this.selfServiceStatus = '请先修改初始密码后再注销账号'
        return
      }
      if (!this.cancelAccountForm.acknowledged) {
        this.selfServiceStatus = '请先勾选已知晓注销后果'
        return
      }
      if (!this.cancelAccountForm.password) {
        this.selfServiceStatus = '请输入当前密码'
        return
      }
      if ((this.cancelAccountForm.confirmText || '').trim() !== '确认注销') {
        this.selfServiceStatus = '请输入：确认注销'
        return
      }
      try {
        await cancelAccount({
          password: this.cancelAccountForm.password,
          confirmText: this.cancelAccountForm.confirmText.trim()
        })
        this.selfServiceStatus = '账号已注销'
        await this.logout()
      } catch (error) {
        this.selfServiceStatus = error.message || '注销失败'
      }
    },
    async submitPayment() {
      if (this.isPermanentMember) {
        this.selfServiceStatus = '终身会员无需缴纳会费'
        return
      }
      const isPermanent = this.paymentFeeChoice === 'PERMANENT' && this.permanentFeeEnabled
      if (isPermanent) {
        if (!this.canPayPermanentFee && !this.canUpgradeToPermanent) {
          this.selfServiceStatus = '当前不可缴纳终身会费'
          return
        }
      } else if (!this.canPayMembershipFee && !this.canRenewMembershipFee && !this.canUpgradeToPermanent) {
        this.selfServiceStatus = '请先提交入会申请并通过秘书处审核后，再缴纳会费'
        return
      }
      try {
        const approvedJoin = (this.myApplications || []).find(item =>
          item.applicationType === 'JOIN' && item.reviewStatus === 'APPROVED')
        const payload = {
          applicationId: this.paymentForm.applicationId || (approvedJoin && approvedJoin.applicationId) || null,
          memberCategory: isPermanent ? 'PERMANENT' : 'ANNUAL'
        }
        const result = await addMyPayment(payload)
        const draft = result.data || result
        if (draft && draft.paymentId) {
          this.uploadSelection.paymentId = draft.paymentId
        }
        await this.loadSelfService()
        const amountText = `${this.membershipFeeCurrencySymbol}${this.membershipFeeAmount}`
        this.selfServiceStatus = isPermanent
          ? `终身会费记录已准备，应缴 ${amountText}，请上传凭证`
          : (this.isExpiredMember
            ? `续费记录已准备，应缴 ${amountText}，请上传凭证`
            : (this.isActiveMember
              ? `提前续费记录已准备，应缴 ${amountText}，请上传凭证`
              : `会费记录已准备，应缴 ${amountText}，请上传凭证`))
      } catch (error) {
        this.selfServiceStatus = error.message
      }
    },
    setConferenceFeedback(message) {
      this.conferenceFeedback = message || ''
      if (message) {
        this.selfServiceStatus = message
      }
    },
    clearConferenceFeedback() {
      this.conferenceFeedback = ''
    },
    /** 当前会议对应身份是否已配置有效注册费（>0） */
    hasUsableConferenceFee(conference) {
      if (!conference) {
        return false
      }
      const quote = this.selectedConferenceFeeQuote
      if (
        quote
        && this.selectedConference
        && String(this.selectedConference.conferenceId) === String(conference.conferenceId)
      ) {
        const recommended = quote.recommendedAmount != null ? Number(quote.recommendedAmount) : NaN
        if (Number.isFinite(recommended)) {
          return recommended > 0
        }
      }
      const amount = this.conferenceUserFeeAmount(conference)
      return amount != null && Number(amount) > 0
    },
    /** 确保会议报名列表已拉取（次要加载是异步的，缴费前必须 await） */
    async ensureMyRegistrationsLoaded() {
      if (!this.isLoggedIn || !localStorage.getItem('paleo_user_token')) {
        return
      }
      try {
        const registrations = await listMyRegistrations()
        this.myRegistrations = registrations.data || []
      } catch (error) {
        // 保留现有列表，由后续报名接口给出明确错误
      }
    },
    attachExistingRegistration(conferenceId) {
      const existing = this.registrationByConferenceId(conferenceId)
      if (!existing || !existing.registrationId) {
        return false
      }
      this.selectedRegistrationId = existing.registrationId
      this.clearConferenceFeedback()
      this.selfServiceStatus = existing.paymentMethod === 'OFFICIAL_CARD_ONSITE'
        ? '该会议已登记公务卡现场支付，可继续填写或修改参会信息'
        : '该会议已锁价报名，请继续上传会议费凭证'
      return true
    },
    async submitRegistration() {
      if (!this.selectedConference) {
        return false
      }
      if (!this.ensureMembershipChoiceCompleted('请先选择参与方式后再报名会议')) {
        return false
      }
      const conferenceId = this.selectedConference.conferenceId
      // 报名前重新获取后端身份报价，避免会员宽限权益或审核状态变化后仍提交旧档位。
      try {
        const quoteResult = await getConferenceFeeQuote(conferenceId)
        const quote = quoteResult && (quoteResult.data || quoteResult)
        if (quote && String(this.selectedConference.conferenceId) === String(conferenceId)) {
          this.selectedConferenceFeeQuote = quote
        }
      } catch (error) {
        // 报价接口短暂失败时保留已有报价；最终仍由后端报名校验兜底。
      }
      if (!this.hasUsableConferenceFee(this.selectedConference)) {
        this.setConferenceFeedback('会议费用未配置或为0，请联系秘书处配置后再报名')
        return false
      }
      await this.ensureMyRegistrationsLoaded()
      if (this.attachExistingRegistration(conferenceId)) {
        return true
      }
      const identity = this.buildRegistrationIdentity()
      try {
        const result = await addMyRegistration({
          conferenceId,
          associationId: this.selectedConference.associationId,
          memberStatus: identity.memberStatus,
          studentStatus: identity.studentStatus,
          feeType: identity.feeType,
          paymentMethod: this.conferencePaymentMethod
        })
        const draft = (result && (result.data || result)) || {}
        await this.loadSelfService()
        await this.ensureMyRegistrationsLoaded()
        // 优先用接口返回的 ID；若旧后端未返回实体，则按当前会议从列表回落匹配
        const created = (draft.registrationId
          ? (this.visibleRegistrations || []).find(item => String(item.registrationId) === String(draft.registrationId))
          : null)
          || this.registrationByConferenceId(conferenceId)
          || (draft.registrationId ? draft : null)
        if (!created || !created.registrationId) {
          this.setConferenceFeedback('报名记录创建失败，请刷新后重试')
          return false
        }
        this.selectedRegistrationId = created.registrationId
        this.clearConferenceFeedback()
        this.selfServiceStatus = created.paymentMethod === 'OFFICIAL_CARD_ONSITE'
          ? '公务卡现场支付已登记，报名已直接通过，可随时填写或修改参会信息'
          : '报名记录已创建，请按线下转账提示汇款并上传会议费凭证'
        return true
      } catch (error) {
        const message = (error && error.message) || '报名失败，请稍后重试'
        // 已报名时后端拒绝重复创建：刷新列表并进入已有报名的第 2 步
        if (/同一会议只能提交一次|已报名|已存在/.test(message)) {
          await this.ensureMyRegistrationsLoaded()
          if (this.attachExistingRegistration(conferenceId)) {
            return true
          }
        }
        this.setConferenceFeedback(message)
        return false
      }
    },
    selectRegistration(registration) {
      if (!registration) {
        return
      }
      this.selectedRegistrationId = registration.registrationId
      const conference = this.publicConferences.find(item => String(item.conferenceId) === String(registration.conferenceId))
      if (conference) {
        this.selectConference(conference)
      }
    },
    registrationByConferenceId(conferenceId) {
      return (this.visibleRegistrations || []).find(item => String(item.conferenceId) === String(conferenceId)) || null
    },
    conferenceFeeRows(conference) {
      const rules = (conference && conference.feeRules) || {}
      const keyMap = [
        { key: 'STUDENT_MEMBER', label: '学生会员', amount: rules.studentMember },
        { key: 'NON_STUDENT_MEMBER', label: '普通会员', amount: rules.nonStudentMember },
        { key: 'STUDENT_NON_MEMBER', label: '学生代表', amount: rules.studentNonMember },
        { key: 'NON_STUDENT_NON_MEMBER', label: '普通代表', amount: rules.nonStudentNonMember }
      ]
      const userKey = this.currentConferenceFeeTypeKey
      return keyMap.map(item => ({
        ...item,
        isUserType: userKey === item.key
      }))
    },
    conferenceUserFeeAmount(conference) {
      const rows = this.conferenceFeeRows(conference)
      const mine = rows.find(item => item.isUserType)
      if (mine && mine.amount != null) {
        return mine.amount
      }
      const quote = this.selectedConferenceFeeQuote
      if (
        quote
        && conference
        && this.selectedConference
        && String(this.selectedConference.conferenceId) === String(conference.conferenceId)
        && quote.recommendedAmount != null
      ) {
        return quote.recommendedAmount
      }
      const fallback = rows.find(item => item.key === 'NON_STUDENT_MEMBER')
      return fallback ? fallback.amount : null
    },
    isConferencePaymentConfirmed(conference) {
      const reg = this.registrationByConferenceId(conference && conference.conferenceId)
      return Boolean(reg && reg.paymentStatus === 'CONFIRMED')
    },
    /** 本地日历日 yyyy-MM-dd，避免 toISOString 的 UTC 偏差 */
    localDateYmd(date = new Date()) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    isDateYmdPassed(value) {
      if (!value) {
        return false
      }
      const day = String(value).slice(0, 10)
      if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) {
        return false
      }
      return this.localDateYmd() > day
    },
    isConferencePaymentDeadlinePassed(conference) {
      if (!conference || !conference.paymentDeadline) {
        return false
      }
      return this.isDateYmdPassed(conference.paymentDeadline)
    },
    isConferenceEnded(conference) {
      if (!conference || !conference.endDate) {
        return false
      }
      return this.isDateYmdPassed(conference.endDate)
    },
    /**
     * 是否仍可新报名/缴纳注册费。
     * 优先用后端派生的 registrationOpen；否则按 status + 截止日本地计算。
     */
    isConferenceRegistrationOpen(conference) {
      if (!conference) {
        return false
      }
      if (typeof conference.registrationOpen === 'boolean') {
        return conference.registrationOpen
      }
      if (String(conference.status || '').toUpperCase() !== 'OPEN') {
        return false
      }
      if (this.isConferencePaymentDeadlinePassed(conference)) {
        return false
      }
      return !this.isConferenceEnded(conference)
    },
    /** 列表标签：已结束 / 缴费已截止 / 正在报名 … */
    conferenceListStatusLabel(conference) {
      if (!conference) {
        return '开放'
      }
      if (this.isConferenceEnded(conference)) {
        return '已结束'
      }
      if (this.isConferencePaymentDeadlinePassed(conference)) {
        return '缴费已截止'
      }
      if (String(conference.status || '').toUpperCase() === 'OPEN') {
        return '正在报名'
      }
      return this.conferenceStatusLabel(conference.status)
    },
    /** 单条报名是否可填白名单（基础信息/摘要/住宿）；野外另见 registrationCanFillFieldTrip */
    registrationCanFillAttendee(registration) {
      if (!registration || registration.status === 'VOIDED'
        || (registration.frozenDueToExpiry === '1' && registration.paymentMethod !== 'OFFICIAL_CARD_ONSITE')) {
        return false
      }
      const s = registration.paymentStatus
      return s === 'INVOICE_PENDING' || s === 'INVOICE_REVIEW' || s === 'CONFIRMED'
    },
    registrationCanFillFieldTrip(registration) {
      if (!registration || registration.status === 'VOIDED'
        || (registration.frozenDueToExpiry === '1' && registration.paymentMethod !== 'OFFICIAL_CARD_ONSITE')) {
        return false
      }
      return registration.paymentStatus === 'CONFIRMED'
    },
    /** 列表 → 查看会议通知（未盖章预览弹窗） */
    async openConferenceNotice(conference) {
      if (!conference) {
        return
      }
      await this.selectConference(conference)
      this.noticePreviewConferenceId = conference.conferenceId
    },
    closeConferenceNotice() {
      this.noticePreviewConferenceId = null
    },
    /** 通知弹窗 → 前往报名（进入详情页） */
    async goRegisterFromNotice(conference) {
      this.closeConferenceNotice()
      await this.openConferenceDetailView(conference)
    },
    /** 列表 →「报名与详情」：会议详细通知页 */
    async openConferenceDetailView(conference) {
      if (!conference) {
        return
      }
      await this.selectConference(conference)
      const registration = this.registrationByConferenceId(conference.conferenceId)
      if (registration) {
        this.selectedRegistrationId = registration.registrationId
      }
      this.conferenceSubView = 'detail'
    },
    /** 列表 →「报名与详情」别名（兼容旧调用） */
    async openConferencePayment(conference) {
      await this.openConferenceDetailView(conference)
    },
    /** 列表 →「缴纳注册费」：创建报名（若无）并进入缴费流 */
    async startConferencePayment(conference) {
      if (!conference) {
        return
      }
      this.clearConferenceFeedback()
      if (!this.isConferenceRegistrationOpen(conference)) {
        if (this.isConferenceEnded(conference)) {
          this.setConferenceFeedback('会议已结束，无法缴纳注册费')
        } else if (this.isConferencePaymentDeadlinePassed(conference)) {
          this.setConferenceFeedback('会议缴费报名已截止')
        } else {
          this.setConferenceFeedback('会议未开放报名')
        }
        return
      }
      if (!this.ensureMembershipChoiceCompleted('请先选择参与方式后再报名会议')) {
        return
      }
      // 分会会议须先绑定；总学会会议除外
      if (Number(conference.associationId) !== 1) {
        const association = (this.bindableAssociations || []).find(
          item => String(item.associationId) === String(conference.associationId)
        )
        if (association && !this.isAssociationBound(association)) {
          this.setConferenceFeedback('请先绑定该会议所属分会后再报名')
          this.goServicesTab('branches')
          return
        }
      }
      await this.selectConference(conference)
      if (!this.hasUsableConferenceFee(conference)) {
        this.setConferenceFeedback('会议费用未配置或为0，请联系秘书处配置后再报名')
        return
      }
      // 报名列表在次要加载中，进入缴费前先同步，避免已报名仍显示第 1 步
      await this.ensureMyRegistrationsLoaded()
      const existing = this.registrationByConferenceId(conference.conferenceId)
      if (existing) {
        this.selectedRegistrationId = existing.registrationId
      } else {
        // 首次进入缴费页停在第 1 步「确认锁价报名」，由用户确认后再创建报名
        this.selectedRegistrationId = null
        this.conferencePaymentMethod = 'BANK_TRANSFER'
      }
      this.clearConferenceFeedback()
      this.conferenceSubView = 'payment'
    },
    /** 详情/列表 → 进入缴费流（已有报名记录时） */
    openConferencePaymentFlow(conference) {
      const target = conference || this.selectedConference
      if (!target) {
        return
      }
      const registration = this.registrationByConferenceId(target.conferenceId)
      if (registration) {
        this.selectedRegistrationId = registration.registrationId
      }
      this.selectConference(target)
      this.conferenceSubView = 'payment'
    },
    /** 详情页 → 立即缴纳注册费 */
    async startPaymentFromDetail() {
      const conference = this.selectedConference
      if (!conference) {
        return
      }
      await this.startConferencePayment(conference)
    },
    /** 缴费卡 →「填写参会资料」：切换到参会资料视图并同步表单 */
    async openAttendeeForm(registration) {
      let current = registration
      if (registration && registration.registrationId) {
        try {
          const registrations = await listMyRegistrations()
          this.myRegistrations = registrations.data || []
          current = this.myRegistrations.find(
            item => String(item.registrationId) === String(registration.registrationId)
          ) || registration
        } catch (error) {
          // 刷新失败时沿用当前数据，后续提交仍由后端状态门禁兜底
        }
      }
      if (!this.registrationCanFillAttendee(current)) {
        this.selfServiceStatus = this.participantLockMessage
          || '会议费凭证审核通过后可先填写参会基础信息、摘要与住宿；逾期或未审过则不可填'
        return
      }
      this.selectedRegistrationId = current.registrationId
      this.participantForm.registrationId = current.registrationId
      const conference = this.publicConferences.find(
        item => String(item.conferenceId) === String(current.conferenceId)
      )
      if (conference) {
        this.selectConference(conference)
      }
      this.syncParticipantFormFromRegistration()
      this.uploadSelection.registrationId = current.registrationId
      this.conferenceSubView = 'attendee'
    },
    /** 返回会议列表（图示三步骤 1） */
    backToConferenceList() {
      this.conferenceSubView = 'list'
      this.closeConferenceNotice()
    },
    /** 参会资料视图 →「返回报名缴费」 */
    backToConferencePayment() {
      this.conferenceSubView = 'payment'
    },
    conferenceFeeTypeLabel(feeType) {
      return {
        STUDENT_MEMBER: '学生会员',
        NON_STUDENT_MEMBER: '普通会员',
        STUDENT_NON_MEMBER: '学生代表',
        NON_STUDENT_NON_MEMBER: '普通代表'
      }[feeType] || '会议费'
    },
    async submitConferenceRegistrationFile(fileRole, file) {
      const registration = this.selectedRegistration
      if (!registration || !file) {
        this.selfServiceStatus = '请先选择文件'
        return false
      }
      if (this.conferencePaymentFileRole !== fileRole) {
        this.selfServiceStatus = '当前状态不允许提交该文件，请刷新页面后重试'
        return false
      }
      if (!this.validateSelectedFile(file, 'payment', fileRole)) {
        return false
      }
      try {
        await uploadMyRegistrationFile(registration.registrationId, fileRole, file)
        await this.loadSelfService()
        this.selectedRegistrationId = registration.registrationId
        this.selfServiceStatus = fileRole === 'voucher'
          ? '会议费凭证已提交，等待管理员初审'
          : '会议费发票已提交，等待财务终审'
        return true
      } catch (error) {
        this.selfServiceStatus = error.message
        return false
      }
    },
    async submitParticipantInfo() {
      if (!this.canFillAttendeeInfo) {
        this.selfServiceStatus = this.participantLockMessage || '当前状态不可填写参会信息'
        return false
      }
      if ((this.participantForm.reportType === 'ORAL' || this.participantForm.reportType === 'POSTER') && !this.participantForm.reportTitle.trim()) {
        this.selfServiceStatus = '口头报告或展板报告必须填写报告题目'
        return false
      }
      if (!this.participantForm.participantName) {
        this.selfServiceStatus = '请填写姓名'
        return false
      }
      if (!this.participantForm.participantUnit) {
        this.selfServiceStatus = '请填写单位'
        return false
      }
      if (!this.participantForm.sessionName) {
        this.selfServiceStatus = '请选择意向分会场/专场后再保存'
        return false
      }
      if (this.participantForm.reportType === 'NONE') {
        this.participantForm.reportTitle = ''
      }
      try {
        const payload = { ...this.participantForm }
        payload.fieldTripStage = this.normalizeFieldTripPhase(payload.fieldTripStage)
        // 白名单阶段不提交野外改动，避免后端拒收
        if (!this.canFillFieldTrip) {
          const reg = this.activeParticipantRegistration || {}
          payload.fieldTripStage = this.normalizeFieldTripPhase(reg.fieldTripStage) || ''
          payload.fieldTripRoute = reg.fieldTripRoute || ''
          payload.fieldTripSelections = reg.fieldTripSelections || ''
        } else if (!payload.fieldTripStage) {
          payload.fieldTripRoute = ''
          payload.fieldTripSelections = ''
        } else if (payload.fieldTripSelections && typeof payload.fieldTripSelections !== 'string') {
          payload.fieldTripSelections = JSON.stringify(payload.fieldTripSelections)
        }
        await updateMyParticipant(payload.registrationId, payload)
        await this.loadSelfService()
        this.selfServiceStatus = this.canFillFieldTrip
          ? '参会资料已保存。会议费已缴清，可在截止日前继续修改；无需再缴费。'
          : '参会资料已保存（基础信息/摘要/住宿）。野外报名须待发票终审通过后提交。'
        return true
      } catch (error) {
        this.selfServiceStatus = error.message || '保存失败，请稍后重试'
        return false
      }
    },
    async deleteAbstractFile() {
      const registrationId = this.participantForm.registrationId || this.uploadSelection.registrationId
      if (!registrationId) {
        this.selfServiceStatus = '请先选择报名记录'
        return
      }
      if (!this.canFillAttendeeInfo) {
        this.selfServiceStatus = this.participantLockMessage || '当前状态不可删除摘要'
        return
      }
      try {
        await deleteMyAbstract(registrationId)
        this.patchRegistrationAbstract(registrationId, {
          abstractFileUrl: '',
          abstractFileName: '',
          abstractSubmitTime: null
        })
        this.selfServiceStatus = '摘要已删除，可重新上传'
      } catch (error) {
        this.selfServiceStatus = error.message || '删除摘要失败'
      }
    },
    /** 就地更新报名摘要字段，避免整表回填冲掉未保存的参会信息 */
    patchRegistrationAbstract(registrationId, patch) {
      const list = this.myRegistrations || []
      const index = list.findIndex(item => String(item.registrationId) === String(registrationId))
      if (index < 0) {
        return
      }
      const next = { ...list[index], ...patch }
      this.$set(this.myRegistrations, index, next)
    },
    async uploadApplicationFile(event) {
      const file = event.target.files[0]
      if (!file || !this.uploadSelection.applicationId) {
        return
      }
      if (!this.validateSelectedFile(file, 'application')) {
        event.target.value = ''
        return
      }
      try {
        await uploadMyApplicationFile(this.uploadSelection.applicationId, file)
        event.target.value = ''
        await this.loadSelfService()
      } catch (error) {
        this.selfServiceStatus = error.message
      }
    },
    async submitMembershipPaymentFile(fileRole, file) {
      if (!file || !this.uploadSelection.paymentId) {
        this.selfServiceStatus = '请先选择文件'
        return false
      }
      if (!this.currentPaymentFileRole || this.currentPaymentFileRole !== fileRole) {
        this.selfServiceStatus = '当前状态不允许提交该文件，请刷新页面后重试'
        return false
      }
      if (!this.validateSelectedFile(file, 'payment', fileRole)) {
        return false
      }
      try {
        await uploadMyPaymentFile(this.uploadSelection.paymentId, fileRole, file)
        await this.loadSelfService()
        this.selfServiceStatus = fileRole === 'voucher'
          ? '凭证已提交，等待管理员初审'
          : '发票已提交，等待财务终审'
        return true
      } catch (error) {
        this.selfServiceStatus = (error && error.message) || '凭证提交失败，请稍后重试'
        return false
      }
    },
    async uploadPaymentFile(event) {
      const file = event.target.files[0]
      if (!file || !this.uploadSelection.paymentId) {
        return
      }
      const role = this.currentPaymentFileRole
      if (!role) {
        event.target.value = ''
        this.selfServiceStatus = '当前状态不允许上传文件，请等待管理员审核'
        return
      }
      if (!this.validateSelectedFile(file, 'payment')) {
        event.target.value = ''
        return
      }
      try {
        await uploadMyPaymentFile(this.uploadSelection.paymentId, role, file)
        event.target.value = ''
        await this.loadSelfService()
      } catch (error) {
        this.selfServiceStatus = error.message
      }
    },
    async uploadAbstractFile(event) {
      const file = event && event.target && event.target.files && event.target.files[0]
      const registrationId = this.participantForm.registrationId || this.uploadSelection.registrationId
      if (!file) {
        this.selfServiceStatus = '请先选择摘要文件'
        return
      }
      if (!registrationId) {
        this.selfServiceStatus = '请先选择报名记录'
        return
      }
      if (!this.canFillAttendeeInfo) {
        this.selfServiceStatus = this.participantLockMessage || '当前状态不可上传摘要'
        return
      }
      if (!this.validateSelectedFile(file, 'word', 'abstract')) {
        if (event && event.target) event.target.value = ''
        return
      }
      try {
        this.uploadSelection.registrationId = registrationId
        const uploaded = await uploadMyRegistrationFile(registrationId, 'abstract', file)
        if (event && event.target) event.target.value = ''
        // 只更新摘要字段，避免 loadSelfService 整表回填冲掉尚未保存的专场/题目等
        this.patchRegistrationAbstract(registrationId, {
          abstractFileUrl: uploaded.url || (uploaded.data && uploaded.data.url) || '',
          abstractFileName: uploaded.fileName || (uploaded.data && uploaded.data.fileName) || file.name || ''
        })
        this.selfServiceStatus = '论文摘要已上传（保留最新版）。专场与报告题目请点「保存参会资料」一并提交。'
      } catch (error) {
        this.selfServiceStatus = error.message || '摘要上传失败'
      }
    },
    async uploadRegistrationFile(event) {
      const file = event.target.files[0]
      if (!file || !this.uploadSelection.registrationId) {
        return
      }
      const type = this.uploadSelection.registrationFileRole === 'abstract' ? 'word' : 'payment'
      if (!this.validateSelectedFile(file, type, this.uploadSelection.registrationFileRole)) {
        event.target.value = ''
        return
      }
      try {
        await uploadMyRegistrationFile(this.uploadSelection.registrationId, this.uploadSelection.registrationFileRole, file)
        event.target.value = ''
        await this.loadSelfService()
      } catch (error) {
        this.selfServiceStatus = error.message
      }
    },
    syncParticipantFormFromRegistration() {
      const registration = this.myRegistrations.find(
        item => String(item.registrationId) === String(this.participantForm.registrationId)
      ) || this.myRegistrations.find(
        item => String(item.registrationId) === String(this.selectedRegistrationId)
      )
      if (!registration) {
        return
      }
      const prev = this.participantForm || {}
      const sameReg = String(prev.registrationId || '') === String(registration.registrationId)
      // 库中为空时保留当前表单未保存内容，避免摘要上传等刷新把专场/题目冲掉
      const keep = (dbVal, formVal, fallback = '') => {
        if (dbVal !== null && dbVal !== undefined && String(dbVal).trim() !== '') {
          return dbVal
        }
        if (sameReg && formVal !== null && formVal !== undefined && String(formVal).trim() !== '') {
          return formVal
        }
        return fallback
      }
      this.participantForm = {
        registrationId: registration.registrationId,
        participantName: keep(registration.participantName, prev.participantName, (this.userInfo && this.userInfo.userName) || ''),
        participantSex: keep(registration.participantSex, prev.participantSex, (this.userInfo && this.userInfo.gender === '女' ? 'FEMALE' : 'MALE')),
        participantUnit: keep(registration.participantUnit, prev.participantUnit, (this.userInfo && this.userInfo.unit) || ''),
        participantRole: keep(registration.participantRole, prev.participantRole, (this.userInfo && this.userInfo.roleLabel) || ''),
        sessionName: keep(registration.sessionName, prev.sessionName, ''),
        reportType: keep(registration.reportType, prev.reportType, 'ORAL'),
        reportTitle: keep(registration.reportTitle, prev.reportTitle, ''),
        accommodationType: keep(registration.accommodationType, prev.accommodationType, 'SELF'),
        fieldTripStage: this.normalizeFieldTripPhase(keep(registration.fieldTripStage, prev.fieldTripStage, '')),
        fieldTripRoute: keep(registration.fieldTripRoute, prev.fieldTripRoute, ''),
        fieldTripSelections: keep(registration.fieldTripSelections, prev.fieldTripSelections, '')
      }
      this.uploadSelection.registrationId = registration.registrationId
    },
    validateSelectedFile(file, type, fileRole) {
      if (!file || file.size === 0 || ((type === 'application' || type === 'word') && file.size < 1024)) {
        this.selfServiceStatus = type === 'application'
          ? '文件为空，请上传已填写的申请书'
          : (type === 'word' ? '文件为空，请上传有效的摘要文件' : '文件为空，请重新选择文件')
        return false
      }
      const extension = (file.name || '').split('.').pop().toLowerCase()
      const allowed = {
        application: ['doc', 'docx', 'pdf'],
        word: ['doc', 'docx'],
        payment: ['pdf', 'jpg', 'jpeg', 'png'],
        voucher: ['jpg', 'jpeg', 'png']
      }[type === 'payment' && fileRole === 'voucher' ? 'voucher' : type] || (type === 'payment' ? ['pdf', 'jpg', 'jpeg', 'png'] : [])
      const maxSize = type === 'application'
        ? 20 * 1024 * 1024
        : type === 'word'
          ? 10 * 1024 * 1024
        : (fileRole === 'voucher' ? 5 * 1024 * 1024 : 10 * 1024 * 1024)
      if (file.size > maxSize) {
        this.selfServiceStatus = type === 'application'
          ? '申请书不能超过 20MB'
          : fileRole === 'voucher' ? '凭证文件不能超过 5MB' : (type === 'word' ? '摘要文件不能超过 10MB' : '发票文件不能超过 10MB')
        return false
      }
      if (!allowed.includes(extension)) {
        this.selfServiceStatus = type === 'application'
          ? '申请书格式不支持。支持格式：.doc / .docx / .pdf，大小不超过 20MB'
          : type === 'word'
            ? '摘要格式不支持。支持格式：.doc / .docx，大小不超过 10MB'
            : (fileRole === 'voucher'
              ? '凭证格式不支持。支持格式：.jpg / .jpeg / .png，大小不超过 5MB'
              : '发票格式不支持。支持格式：.pdf / .jpg / .jpeg / .png，大小不超过 10MB')
        return false
      }
      return true
    },
    buildRegistrationIdentity() {
      const isStudent = this.userInfo && String(this.userInfo.isStudent) === '1'
      const isActiveMember = this.memberProfile && this.memberProfile.memberStatus === 'ACTIVE'
      const localFeeType = isStudent
        ? (isActiveMember ? 'STUDENT_MEMBER' : 'STUDENT_NON_MEMBER')
        : (isActiveMember ? 'NON_STUDENT_MEMBER' : 'NON_STUDENT_NON_MEMBER')
      const quotedFeeType = this.selectedConferenceFeeQuote && this.selectedConferenceFeeQuote.recommendedFeeType
      const validFeeTypes = [
        'STUDENT_MEMBER',
        'NON_STUDENT_MEMBER',
        'STUDENT_NON_MEMBER',
        'NON_STUDENT_NON_MEMBER'
      ]
      const feeType = validFeeTypes.includes(quotedFeeType) ? quotedFeeType : localFeeType
      const memberStatus = feeType === 'STUDENT_MEMBER' || feeType === 'NON_STUDENT_MEMBER'
        ? 'MEMBER'
        : 'NON_MEMBER'
      const studentStatus = feeType === 'STUDENT_MEMBER' || feeType === 'STUDENT_NON_MEMBER'
        ? 'STUDENT'
        : 'NON_STUDENT'
      return {
        memberStatus,
        studentStatus,
        feeType
      }
    },
    resolveBannerImage(banner) {
      if (!banner) return ''
      const url = banner.coverUrl || banner.mediaUrl || banner.fileUrl
      if (!url) return ''
      return banner.localImage ? url : this.resolveFileUrl(url)
    },
    resolveFileUrl(url) {
      if (!url || /^https?:\/\//.test(url)) {
        return url
      }
      const base = process.env.VUE_APP_BASE_API || ''
      const path = toSiteRelativeFilePath(url, base) || (url.startsWith('/') ? url : `/${url}`)
      return `${base}${path.startsWith('/') ? path : `/${path}`}`
    },
    /**
     * 可供 img / CSS background 直接使用的地址。
     * /storage/files|/profile 走匿名 CMS 公开代理，避免鉴权接口暴露给浏览器直链。
     */
    resolvePublicDisplayUrl(url) {
      const raw = String(url || '').trim()
      if (!raw) {
        return ''
      }
      if (/^https?:\/\//i.test(raw) && !this.normalizeSiteFilePath(raw)) {
        return raw
      }
      const relative = this.normalizeSiteFilePath(raw)
      const path = (relative || (raw.startsWith('/') ? raw : `/${raw}`)).replace(/[?#].*$/, '')
      if (isStorageOrProfilePath(path)) {
        const base = process.env.VUE_APP_BASE_API || ''
        return `${base}/paleo/cms/public/file?url=${encodeURIComponent(path)}`
      }
      return this.resolveFileUrl(path)
    },
    fileNameFromUrl(url) {
      const path = (url || '').split('?')[0]
      // /storage/files/{id} 无真实文件名，勿再误标为「申请书文件」
      if (/\/storage\/files\/\d+$/i.test(path)) {
        return ''
      }
      const raw = path.split('/').pop()
      if (!raw || /^\d+$/.test(raw)) {
        return ''
      }
      try {
        return decodeURIComponent(raw)
      } catch (e) {
        return raw
      }
    },
    parseRouteOptions(value) {
      const raw = value || ''
      if (String(raw).trim().startsWith('[') || String(raw).trim().startsWith('{')) {
        try {
          const parsed = JSON.parse(raw)
          const routes = Array.isArray(parsed) ? parsed : (parsed.routes || [])
          return routes.map(item => ({
            id: item.id || item.name,
            name: item.name,
            phase: this.normalizeFieldTripPhase(item.phase) || 'pre',
            fee: Number(item.fee !== undefined ? item.fee : item.routeFee) || 0,
            genderRestriction: item.genderRestriction || 'any'
          })).filter(item => item.name).slice(0, 15)
        } catch (error) {
          return []
        }
      }
      return String(raw)
        .split(/\r?\n|；|;/)
        .map((name, index) => ({ id: `legacy-${index}`, name: name.trim(), phase: 'pre', fee: 0, genderRestriction: 'any' }))
        .filter(item => item.name)
        .slice(0, 15)
    },
    /**
     * 野外阶段全站统一：pre / during / post（会前/会中/会后）。
     * 读时兼容历史 BEFORE/DURING/AFTER。
     */
    normalizeFieldTripPhase(value) {
      if (value === null || value === undefined || value === '') {
        return ''
      }
      const lower = String(value).trim().toLowerCase()
      if (lower === 'pre' || lower === 'before') return 'pre'
      if (lower === 'during' || lower === 'in') return 'during'
      if (lower === 'post' || lower === 'after') return 'post'
      return ''
    },
    parseSessionOptions(value) {
      const raw = value || ''
      if (!String(raw).trim()) {
        return []
      }
      if (String(raw).trim().startsWith('[') || String(raw).trim().startsWith('{')) {
        try {
          const parsed = JSON.parse(raw)
          const sessions = Array.isArray(parsed) ? parsed : (parsed.sessions || [])
          return sessions
            .map(item => ({
              id: item.id || item.name,
              name: (item.name || '').trim()
            }))
            .filter(item => item.name)
        } catch (error) {
          return []
        }
      }
      return String(raw)
        .split(/\r?\n|；|;/)
        .map((name, index) => ({ id: `session-${index}`, name: name.trim() }))
        .filter(item => item.name)
    },
    syncProfileForm(user) {
      const isStudent = user && String(user.isStudent) === '1'
      this.profileForm = {
        userName: user.userName || user.nickName || '',
        gender: user.gender || '',
        unit: user.unit || '',
        orgCreditCode: user.orgCreditCode || '',
        roleLabel: user.roleLabel || '教师',
        title: user.title || '',
        email: user.email || '',
        memberCategory: isStudent ? 'STUDENT' : 'NON_STUDENT'
      }
      if (this.paymentFeeChoice === 'CORPORATE') {
        this.paymentFeeChoice = 'ANNUAL'
      }
    }
  }
}
